import React, { useEffect, useState, useRef } from 'react';

function PinchZoomImage({ src, alt }) {
  const imgRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const [state, setState] = React.useState({ scale: 1, lastScale: 1, x: 0, y: 0, lastX: 0, lastY: 0, touches: 0 });

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let startDist = 0, startScale = 1, startX = 0, startY = 0, lastX = 0, lastY = 0;
    let isPanning = false;
    function getDist(e) {
      const [a, b] = e.touches;
      return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    }
    function onTouchStart(e) {
      if (e.touches.length === 2) {
        startDist = getDist(e);
        startScale = state.scale;
      } else if (e.touches.length === 1 && state.scale > 1) {
        isPanning = true;
        startX = e.touches[0].clientX - state.x;
        startY = e.touches[0].clientY - state.y;
      }
      setState(s => ({ ...s, touches: e.touches.length }));
    }
    function onTouchMove(e) {
      if (e.touches.length === 2) {
        const dist = getDist(e);
        let newScale = Math.max(1, Math.min(4, startScale * (dist / startDist)));
        setState(s => ({ ...s, scale: newScale, lastScale: newScale }));
      } else if (e.touches.length === 1 && isPanning && state.scale > 1) {
        let x = e.touches[0].clientX - startX;
        let y = e.touches[0].clientY - startY;
        setState(s => ({ ...s, x, y, lastX: x, lastY: y }));
      }
      e.preventDefault();
    }
    function onTouchEnd(e) {
      isPanning = false;
      setState(s => ({ ...s, touches: e.touches.length }));
    }
    container.addEventListener('touchstart', onTouchStart, { passive: false });
    container.addEventListener('touchmove', onTouchMove, { passive: false });
    container.addEventListener('touchend', onTouchEnd);
    return () => {
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchmove', onTouchMove);
      container.removeEventListener('touchend', onTouchEnd);
    };
  }, [state.scale, state.x, state.y]);
  return (
    <div
      ref={containerRef}
      style={{
        touchAction: 'none',
        width: '100vw',
        height: '75vh',
        maxWidth: '100vw',
        maxHeight: '85vh',
        overflow: 'hidden',
        background: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        style={{
          transform: `scale(${state.scale}) translate(${state.x / state.scale}px, ${state.y / state.scale}px)`,
          transition: state.touches === 0 ? 'transform 0.2s' : 'none',
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain',
          userSelect: 'none',
          touchAction: 'none',
        }}
        draggable={false}
      />
    </div>
  );
}

export default function VehicleDetailAdmin() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  // Extract the dealership ID and VIN from the URL
  const pathParts = window.location.pathname.split('/');
  const dealershipId = pathParts[3]; // e.g., /admin/vehicle/DEALERSHIPID/VIN
  const vin = pathParts[4];

  const [vehicle, setVehicle] = useState(null);
  const [dealership, setDealership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [navVisible, setNavVisible] = useState(false);

  // Calculator state
  const [paymentType, setPaymentType] = useState('finance');
  const [calcDownPayment, setCalcDownPayment] = useState('');
  const [calcTerm, setCalcTerm] = useState(60);
  const [calcCreditScore, setCalcCreditScore] = useState(700);
  const [calcApr, setCalcApr] = useState(5.99);
  const [includeTaxes, setIncludeTaxes] = useState(false);
  const [showTradeIn, setShowTradeIn] = useState(false);
  const [tradeInValue, setTradeInValue] = useState(0);
  const [zipCode, setZipCode] = useState('');
  const [taxRate, setTaxRate] = useState(0);
  const [taxRegion, setTaxRegion] = useState('');
  const [taxState, setTaxState] = useState('');
  const [taxLoading, setTaxLoading] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [inspectionPdfUrl, setInspectionPdfUrl] = useState('');

  // New state for active tab
  const [activeTab, setActiveTab] = useState('General Info.');

  // New state for inspection modal
  const [showInspectionModal, setShowInspectionModal] = useState(false);

  // New state for send packet modal
  const [showSendPacketModal, setShowSendPacketModal] = useState(false);
  const [customMessage, setCustomMessage] = useState('');
  const [packetOptions, setPacketOptions] = useState([
    { value: 'module1', label: 'Module 1' },
    { value: 'module2', label: 'Module 2' },
    { value: 'module3', label: 'Module 3' },
  ]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  // Add state for unique link (remove sendMethod)
  const [lastGeneratedLink, setLastGeneratedLink] = useState('');

  // Add state for customer search/add/select
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [newCustomer, setNewCustomer] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  const [pastedCustomerText, setPastedCustomerText] = useState('');
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [customerAdded, setCustomerAdded] = useState(false);

  // Add state for modal photo viewer
  const [photoModal, setPhotoModal] = useState({ open: false, index: 0 });  

  // Add state for autoCheckPdfUrl and msrpPdfUrl
  const [autoCheckPdfUrl, setAutoCheckPdfUrl] = useState('');
  const [msrpPdfUrl, setMsrpPdfUrl] = useState('');
  const [uploadingAutoCheck, setUploadingAutoCheck] = useState(false);
  const [uploadingMsrp, setUploadingMsrp] = useState(false);

  // New state for AutoCheck History modal
  const [showAutoCheckModal, setShowAutoCheckModal] = useState(false);

  // New state for Original MSRP / Options modal
  const [showMsrpModal, setShowMsrpModal] = useState(false);

  // New state for copied link
  const [copied, setCopied] = useState(false);

  // New state for email template
  const [showEmailTemplate, setShowEmailTemplate] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);

  // New state for packet templates
  const [selectedTemplate, setSelectedTemplate] = useState('default');
  const [availableTemplates] = useState([
    { id: 'default', name: 'Default', description: 'Full calculator with financing and cash options' },
    { id: 'cash-only', name: 'Cash Only', description: 'Cash payment only, no financing options' }
  ]);

  // Animation refs for space background
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const starsRef = useRef([]);

  // Responsive state for mobile nav
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 640);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent background scroll when Send Packet modal is open
  useEffect(() => {
    if (showSendPacketModal) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = original; };
    }
  }, [showSendPacketModal]);

  // Prevent background scroll when Email Template modal is open
  useEffect(() => {
    if (showEmailTemplate) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = original; };
    }
  }, [showEmailTemplate]);

  // Check authentication on component mount
  useEffect(() => {
    const isLoggedIn = localStorage.getItem('adminLoggedIn');
    if (!isLoggedIn) {
      window.location.replace('/admin/login');
      return;
    }
    setIsAuthenticated(true);
    setIsCheckingAuth(false);
  }, []);

  // Place all useEffect hooks here
  useEffect(() => {
    // Fetch vehicle data
    fetch(`/api/dealerships/${dealershipId}/vehicle/${vin}`)
      .then(res => res.json())
      .then(data => {
        setVehicle(data);
        setLoading(false);
      });
    
    // Fetch dealership data
    fetch(`/api/dealerships`)
      .then(res => res.json())
      .then(dealerships => {
        const currentDealership = dealerships.find(d => d._id === dealershipId);
        setDealership(currentDealership);
      });
    
    setTimeout(() => setNavVisible(true), 100);
  }, [dealershipId, vin]);

  useEffect(() => {
    if (paymentType === 'finance') {
      setCalcApr(getAprForCreditScore(calcCreditScore, paymentType));
    }
  }, [calcCreditScore, paymentType]);

  useEffect(() => {
    if (!dealershipId || !vin) return;
    fetch(`/api/dealerships/${dealershipId}/settings`)
      .then(res => res.json())
      .then(settings => {
        if (settings && settings.vehicleVideos) {
          // Try all case variants
          const direct = settings.vehicleVideos[vin];
          const upper = settings.vehicleVideos[vin?.toUpperCase()];
          const lower = settings.vehicleVideos[vin?.toLowerCase()];
          setVideoUrl(direct || upper || lower || '');
        } else {
          setVideoUrl('');
        }
        // Inspection PDF
        const vinSafe = vin.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
        if (settings && settings.inspectionPdfs) {
          const pdfUrl = settings.inspectionPdfs[vinSafe] || '';
          setInspectionPdfUrl(pdfUrl);
        } else {
          setInspectionPdfUrl('');
        }
        // AutoCheck PDF
        if (settings && settings.autoCheckPdfs) {
          setAutoCheckPdfUrl(settings.autoCheckPdfs[vinSafe] || '');
        } else {
          setAutoCheckPdfUrl('');
        }
        // MSRP PDF
        if (settings && settings.msrpPdfs) {
          setMsrpPdfUrl(settings.msrpPdfs[vinSafe] || '');
        } else {
          setMsrpPdfUrl('');
        }
      });
  }, [dealershipId, vin]);

  useEffect(() => {
    if (vehicle && vin) {
      const link = `${window.location.origin}/vehicle-packet/${vehicle.dealership}/${vin}/${Math.random().toString(36).substr(2, 9)}`;
      setLastGeneratedLink(link);
    }
  }, [vehicle, vin]);

  useEffect(() => {
    if (!selectedCustomer) {
      setLastGeneratedLink('');
      return;
    }
    const link = generateUniqueLinkAndLog(selectedCustomer);
    setLastGeneratedLink(link);
  }, [selectedCustomer]);

  // Update customer search effect to show both customers and leads when searching, merged by email
  useEffect(() => {
    if (!customerSearch) {
      setCustomerResults([]);
      return;
    }
    setCustomerSearchLoading(true);
    
    // Get user info from localStorage for role-based filtering
    const userRole = localStorage.getItem('adminRole');
    const userEmail = localStorage.getItem('adminEmail');
    const userId = localStorage.getItem('adminId');
    
    // Create query string with user info for role-based filtering
    const queryParams = new URLSearchParams({
      userRole: userRole || '',
      userEmail: userEmail || '',
      userId: userId || '',
      userName: localStorage.getItem('adminName') || 'User'
    });
    
    Promise.all([
      fetch(`/api/dealerships/${dealershipId}/customers?search=${encodeURIComponent(customerSearch)}&${queryParams}`).then(res => res.json()),
      fetch(`/api/dealerships/${dealershipId}/leads?${queryParams}`).then(res => res.json())
    ]).then(([customers, leadsData]) => {
      const leads = Array.isArray(leadsData.leads) ? leadsData.leads : [];
      // Filter leads to only those matching the search
      const searchLower = customerSearch.toLowerCase().trim();
      const filteredLeads = leads.filter(l => {
        // Detect if search term looks like an email (contains @ and no spaces)
        const looksLikeEmail = searchLower.includes('@') && !searchLower.includes(' ');
        
        if (looksLikeEmail) {
          // If it looks like an email, only search email field
          return l.email && l.email.toLowerCase().includes(searchLower);
        } else {
          // Otherwise, focus on name searching (first name, last name, full names)
          const firstNameMatch = l.firstName && l.firstName.toLowerCase().includes(searchLower);
          const lastNameMatch = l.lastName && l.lastName.toLowerCase().includes(searchLower);
          
          // Full name search (first + last)
          const fullName = `${l.firstName || ''} ${l.lastName || ''}`.toLowerCase().trim();
          const fullNameMatch = fullName.includes(searchLower);
          
          // Reverse full name search (last + first) for searches like "Smith John"
          const reverseFullName = `${l.lastName || ''} ${l.firstName || ''}`.toLowerCase().trim();
          const reverseFullNameMatch = reverseFullName.includes(searchLower);
          
          // Also include phone search as secondary
          const phoneMatch = l.phone && l.phone.toLowerCase().includes(searchLower);
          
          return firstNameMatch || lastNameMatch || fullNameMatch || reverseFullNameMatch || phoneMatch;
        }
      });
      // Merge by email (customers take priority)
      const all = [
        ...customers,
        ...filteredLeads.filter(l => !customers.some(c => c.email === l.email)).map(l => ({ ...l, leadId: l._id }))
      ];
      setCustomerResults(all);
      setCustomerSearchLoading(false);
    });
  }, [customerSearch, dealershipId]);

  // Initialize stars
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    // Create stars
    const stars = [];
    for (let i = 0; i < 150; i++) {
      stars.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 1.5 + 0.3,
        speed: Math.random() * 0.3 + 0.05,
        opacity: Math.random() * 0.6 + 0.2,
        twinkle: Math.random() * 0.02 + 0.005
      });
    }
    starsRef.current = stars;
    
    // Handle resize
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Mouse tracking
  useEffect(() => {
    const handleMouseMove = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Animation loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const stars = starsRef.current;
    
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw moving stars
      stars.forEach(star => {
        // Move stars
        star.y += star.speed;
        if (star.y > canvas.height) {
          star.y = -5;
          star.x = Math.random() * canvas.width;
        }
        
        // Twinkle effect
        star.opacity += star.twinkle;
        if (star.opacity > 1 || star.opacity < 0.1) {
          star.twinkle = -star.twinkle;
        }
        
        // Mouse interaction - subtle attraction to mouse
        const dx = mouseRef.current.x - star.x;
        const dy = mouseRef.current.y - star.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < 100) {
          const force = (100 - distance) / 100;
          star.x += dx * force * 0.005;
          star.y += dy * force * 0.005;
        }
        
        // Draw star
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity})`;
        ctx.fill();
        
        // Add subtle glow effect
        if (star.size > 1) {
          ctx.beginPath();
          ctx.arc(star.x, star.y, star.size * 1.5, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(135, 206, 250, ${star.opacity * 0.2})`;
          ctx.fill();
        }
      });
      
      // Draw subtle nebula effect around mouse
      const gradient = ctx.createRadialGradient(
        mouseRef.current.x, mouseRef.current.y, 0,
        mouseRef.current.x, mouseRef.current.y, 150
      );
      gradient.addColorStop(0, 'rgba(138, 43, 226, 0.03)');
      gradient.addColorStop(0.5, 'rgba(75, 0, 130, 0.02)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  function getAprForCreditScore(score, type) {
    if (type === 'cash') return 0;
    if (score >= 750) return 3.99;
    if (score >= 700) return 5.99;
    if (score >= 650) return 7.99;
    return 9.99;
  }

  // Tax rate lookup function
  const lookupTaxRate = async (zip) => {
    if (!zip || zip.length !== 5) {
      setTaxRate(0);
      setTaxRegion('');
      setTaxState('');
      return;
    }
    setTaxLoading(true);
    try {
      const response = await fetch(`/api/dealerships/utils/tax-rate/${zip}`);
      const data = await response.json();
      if (response.ok) {
        setTaxRate(data.rate * 100); // Convert to percentage
        setTaxRegion(data.regionName);
        setTaxState(data.state);
      } else {
        setTaxRate(0);
        setTaxRegion('');
        setTaxState('');
      }
    } catch (error) {
      setTaxRate(0);
      setTaxRegion('');
      setTaxState('');
    }
    setTaxLoading(false);
  };

  const handleZipCodeChange = (e) => {
    const newZip = e.target.value.replace(/\D/g, '').slice(0, 5);
    setZipCode(newZip);
    if (newZip.length === 5) {
      lookupTaxRate(newZip);
    } else {
      setTaxRate(0);
      setTaxRegion('');
      setTaxState('');
    }
  };

  // Don't render anything if still checking authentication or not authenticated
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-lg">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null; // Will redirect in useEffect
  }

  // After all hooks, then do:
  if (loading) return <div>Loading...</div>;
  if (!vehicle) return <div>Vehicle not found.</div>;

  // Use the same logic as DealershipShowcase.jsx
  const year = vehicle.year || '';
  const make = vehicle.make || '';
  const model = vehicle.model || '';
  const trim = vehicle.trim || '';
  let price = vehicle.price || vehicle.internetPrice || vehicle.msrp || '';
  if (typeof price === 'string') {
    price = price.replace(/[^0-9.]/g, '');
  }
  if (!price || isNaN(Number(price)) || Number(price) <= 0) {
    price = 'Call for Price';
  } else {
    price = Number(price);
  }
  const mileage = vehicle.mileage ? Number(vehicle.mileage).toLocaleString() : 'N/A';
  const exteriorColor = vehicle.exteriorColor || 'N/A';
  const interiorColor = vehicle.interiorColor || 'N/A';
  const body = vehicle.body || vehicle.bodyType || 'N/A';
  const drivetrain = vehicle.drivetrain || vehicle.drive || 'N/A';
  const stockNumber = vehicle.stockNumber || 'N/A';
  const vinField = vehicle.vin || 'N/A';
  const imageOne = vehicle.imageOne && vehicle.imageOne.startsWith('http') ? vehicle.imageOne : '';
  const imageUrls = vehicle.imageUrls || '';
  const gallery = imageUrls.split(',').map(url => url.trim()).filter(url => url.startsWith('http'));
  const mainImage = imageOne || gallery[0] || 'https://via.placeholder.com/600x350?text=No+Image';
  const description = vehicle.description || 'No description available.';

  // Calculator logic
  const calcVehiclePrice = typeof price === 'number' ? price : 0;
  const fallbackTaxRate = 7;
  const effectiveTaxRate = (includeTaxes && (taxRate > 0 ? taxRate : fallbackTaxRate)) || 0;
  const hasTradeIn = showTradeIn && tradeInValue && !isNaN(tradeInValue) && Number(tradeInValue) > 0;
  const taxableAmount = hasTradeIn ? Math.max(calcVehiclePrice - Number(tradeInValue), 0) : calcVehiclePrice;
  const calcTaxes = includeTaxes ? Math.round(taxableAmount * (effectiveTaxRate / 100)) : 0;
  const calcFees = 245.28;
  const calcMonthlyPayment =
    paymentType === 'finance' && calcVehiclePrice
      ? (
          (((calcVehiclePrice + calcTaxes + calcFees - calcDownPayment - tradeInValue) * (calcApr / 100 / 12) *
            Math.pow(1 + calcApr / 100 / 12, calcTerm)) /
          (Math.pow(1 + calcApr / 100 / 12, calcTerm) - 1))
        ) || 0
      : 0;

  // Create dynamic navigation links based on available PDFs
  const navLinks = [
    { label: 'General Info.' },
    ...(autoCheckPdfUrl ? [{ label: 'AutoCheck History' }] : []),
    ...(inspectionPdfUrl ? [{ label: 'Inspection Forms' }] : []),
    ...(msrpPdfUrl ? [{ label: 'Original MSRP / Options' }] : []),
  ];

  // Height of nav bar + accent line for spacing
  const navBarHeight = 84;
  const accentHeight = 6;
  const totalNavHeight = navBarHeight + accentHeight;

  const handleSendPacket = async () => {
    setSending(true);
    const link = `${window.location.origin}/vehicle-packet/${vehicle.dealership}/${vin}/${Math.random().toString(36).substr(2, 9)}`;
    setLastGeneratedLink(link);
    const vehicleSummary = `${year} ${make} ${model} ${trim} (VIN: ${vinField})`;
    console.log('Sending packet...', { link, customMessage, vehicleSummary });
    try {
      const res = await fetch('/api/dealerships/api/packet-logs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          link,
          customMessage,
          vehicleSummary,
          dealershipId,
          email: selectedCustomer?.email, // Always include email if available
          customerId: selectedCustomer?._id || selectedCustomer?.id,
          vehicle
        }),
      });
      console.log('Response:', res);
      if (res.ok) {
        setSendResult('success');
      } else {
        setSendResult('error');
      }
    } catch (err) {
      console.error('Send packet error:', err);
      setSendResult('error');
    }
    setSending(false);
  };

  const toggleOption = (value) => {
    if (selectedOptions.includes(value)) {
      setSelectedOptions(selectedOptions.filter(opt => opt !== value));
    } else {
      setSelectedOptions([...selectedOptions, value]);
    }
  };

  // Add new customer
  async function handleAddCustomer(e) {
    e.preventDefault();
    
    // Get user info from localStorage for createdBy field
    const userRole = localStorage.getItem('adminRole');
    const userEmail = localStorage.getItem('adminEmail');
    const userName = localStorage.getItem('adminName');
    const userId = localStorage.getItem('adminId');
    
    const customerData = {
      ...newCustomer,
      createdBy: {
        email: userEmail,
        name: userName,
        role: userRole,
        id: userId
      }
    };
    
    const res = await fetch(`/api/dealerships/${dealershipId}/customers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(customerData),
    });
    let customer = null;
    if (res.status === 409) {
      const data = await res.json();
      alert('Customer already exists for this dealership.');
      customer = data.customer;
    } else if (res.ok) {
      customer = await res.json();
      setCustomerAdded(true);
      setTimeout(() => setCustomerAdded(false), 2000);
      // Get user info from localStorage for createdBy field
      const userRole = localStorage.getItem('adminRole');
      const userEmail = localStorage.getItem('adminEmail');
      const userName = localStorage.getItem('adminName');
      const userId = localStorage.getItem('adminId');
      
      const leadRes = await fetch(`/api/dealerships/${dealershipId}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: newCustomer.firstName,
          lastName: newCustomer.lastName,
          email: newCustomer.email,
          phone: newCustomer.phone,
          zip: newCustomer.zip || '',
          dealershipId,
          vehicle: vehicle, // or null if not tied to a vehicle
          createdBy: {
            email: userEmail,
            name: userName,
            role: userRole,
            id: userId
          }
        })
      });
      
      if (!leadRes.ok) {
        console.error('Error creating lead:', await leadRes.text());
      }
    } else {
      const err = await res.json();
      alert('Error adding customer: ' + (err.error || 'Unknown error'));
      console.error('Add customer error:', err);
      return;
    }
    
    // Auto-select the customer (either newly created or existing)
    if (customer) {
      setSelectedCustomer(customer);
      // Set the search field to show the customer's name
      setCustomerSearch(`${customer.firstName} ${customer.lastName} (${customer.email})`);
    }
    
    setCustomerResults([]);
    setNewCustomer({ firstName: '', lastName: '', email: '', phone: '' });
    setPastedCustomerText('');
  }

  // When generating a new link, also create a packet log
  function generateUniqueLinkAndLog(customer) {
    if (!vehicle || !vin || !customer) return '';
    const linkId = Math.random().toString(36).substr(2, 9);
    const fullUrl = `${window.location.origin}/vehicle-packet/${vehicle.dealership}/${vin}/${linkId}`;
    const pathUrl = `/vehicle-packet/${vehicle.dealership}/${vin}/${linkId}`;
    
    // Create packet log with path format (to match handle-open lookup)
    const userInfo = {
      name: localStorage.getItem('adminName') || 'Unknown',
      email: localStorage.getItem('adminEmail') || 'Unknown',
      role: localStorage.getItem('adminRole') || 'ADMIN',
      id: localStorage.getItem('adminId') || 'system'
    };
    
    fetch('/api/dealerships/api/packet-logs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        customerId: customer._id || customer.id,
        link: pathUrl, // Store path format instead of full URL
        linkId: linkId, // Include the linkId for matching
        vehicle,
        dealershipId,
        email: customer.email, // Always include email
        createdBy: userInfo,
        template: selectedTemplate // Add template information
      }),
    })
    .then(response => response.json())
    .then(data => {
      console.log('Packet log created successfully:', data);
    })
    .catch(error => {
      console.error('Error creating packet log:', error);
    });

    // Track URL creation activity
    fetch('/api/dealerships/api/url-activity', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        createdBy: {
          name: userInfo.name,
          email: userInfo.email,
          role: userInfo.role
        },
        dealership: vehicle.dealership,
        dealershipName: dealership?.name || 'Unknown Dealership',
        customer: {
          firstName: customer.firstName,
          lastName: customer.lastName,
          email: customer.email,
          phone: customer.phone
        },
        vehicle: {
          vin: vehicle.vin,
          year: vehicle.year,
          make: vehicle.make,
          model: vehicle.model,
          stockNumber: vehicle.stockNumber
        },
        customUrl: fullUrl,
        linkId: linkId
      }),
    });
    
    return fullUrl;
  }

  // Update openSendPacketModal and handleGenerateNewLink to require a customer
  const openSendPacketModal = () => {
    setCustomMessage(''); // Empty default message
    setShowSendPacketModal(true);
    setSelectedCustomer(null);
    setCustomerSearch('');
    setCustomerResults([]);
    setNewCustomer({ firstName: '', lastName: '', email: '', phone: '' });
    setPastedCustomerText('');
    setLastGeneratedLink('');
    setSelectedTemplate('default'); // Reset template selection
  };

  const handleGenerateNewLink = () => {
    if (!selectedCustomer) return;
    const link = generateUniqueLinkAndLog(selectedCustomer);
    setLastGeneratedLink(link);
  };

  // Upload handlers
  const handleAutoCheckPdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingAutoCheck(true);
    const formData = new FormData();
    formData.append('pdf', file);
    formData.append('vin', vin);
    const res = await fetch(`/api/dealerships/${dealershipId}/settings/autocheck-pdf`, {
      method: 'POST',
      body: formData,
    });
    const data = await res.json();
    if (data.url) setAutoCheckPdfUrl(data.url);
    setUploadingAutoCheck(false);
  };

  const handleMsrpPdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingMsrp(true);
    const formData = new FormData();
    formData.append('pdf', file);
    formData.append('vin', vin);
    const res = await fetch(`/api/dealerships/${dealershipId}/settings/msrp-pdf`, {
      method: 'POST',
      body: formData,
    });
    const data = await res.json();
    if (data.url) setMsrpPdfUrl(data.url);
    setUploadingMsrp(false);
  };

  // Function to parse pasted customer text and auto-fill form
  const parseCustomerText = (text) => {
    if (!text.trim()) return;

    // Extract email (more specific pattern)
    const emailMatch = text.match(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/);
    const email = emailMatch ? emailMatch[0] : '';

    // Extract phone number (various formats)
    const phoneMatch = text.match(/(?:C:|Cell:|Phone:|Mobile:|Tel:)?\s*\(?(\d{3})\)?[-.\s]*(\d{3})[-.\s]*(\d{4})/i) || 
                     text.match(/\(?(\d{3})\)?[-.\s]*(\d{3})[-.\s]*(\d{4})/);
    const phone = phoneMatch ? `(${phoneMatch[1]}) ${phoneMatch[2]}-${phoneMatch[3]}` : '';

    // Extract name from first line (most reliable approach)
    const lines = text.split('\n').map(line => line.trim()).filter(line => line);
    let firstName = '';
    let lastName = '';
    
    if (lines.length > 0) {
      const firstLine = lines[0];
      // Remove common prefixes and suffixes
      const cleanedLine = firstLine.replace(/\(.*?\)/g, '').trim();
      const nameParts = cleanedLine.split(/\s+/);
      
      if (nameParts.length >= 2) {
        firstName = nameParts[0];
        lastName = nameParts.slice(1).join(' '); // Join remaining parts as last name
      } else if (nameParts.length === 1) {
        firstName = nameParts[0];
      }
    }

    // Update the form with extracted data
    setNewCustomer({
      firstName: firstName,
      lastName: lastName,
      email: email,
      phone: phone
    });
  };

  // Generate email template
  const generateEmailTemplate = () => {
    if (!selectedCustomer || !vehicle || !lastGeneratedLink) return '';
    
    const customerName = `${selectedCustomer.firstName}`.trim();
    const vehicleInfo = `${year} ${make} ${model} ${trim}`.trim();
    const fullUrl = lastGeneratedLink;
    const priceDisplay = typeof price === 'number' ? `$${price.toLocaleString()}` : price;
    
    return `Hi ${customerName || 'Valued Customer'},

${customMessage.trim() ? customMessage.trim() : `Thanks for your interest in our ${vehicleInfo}. I've created a personalized vehicle package just for you with all the details, photos, and payment options.`}

🚗 ${vehicleInfo}
💰 ${priceDisplay}
📍 Stock #: ${stockNumber}
🛣️ ${mileage} miles

👉 Vehicle Details Link: ${fullUrl}

This link includes photos, specs, payment calculator, and all vehicle information. Feel free to contact me with any questions or to schedule a test drive.`;
  };

  // Generate HTML email template
  const generateHTMLEmailTemplate = () => {
    if (!selectedCustomer || !vehicle || !lastGeneratedLink) return '';
    
    const customerName = `${selectedCustomer.firstName}`.trim();
    const vehicleInfo = `${year} ${make} ${model} ${trim}`.trim();
    const fullUrl = lastGeneratedLink;
    const priceDisplay = typeof price === 'number' ? `$${price.toLocaleString()}` : price;
    
    return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<title>Your ${vehicleInfo} Information Package</title>
</head>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f9fafb;">

<div style="background: linear-gradient(135deg, #1e3a8a, #3b82f6); color: white; padding: 25px; border-radius: 15px; text-align: center; margin-bottom: 25px;">
  <h1 style="margin: 0; font-size: 28px;">${vehicleInfo}</h1>
  <p style="margin: 10px 0 0 0; opacity: 0.9; font-size: 18px;">Click the photos or The Vehicle Details Link</p>
</div>

<div style="background: white; border-radius: 15px; padding: 30px; margin-bottom: 25px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
  <p style="margin: 0 0 15px 0; font-size: 16px;"><strong>Hi ${customerName || 'Valued Customer'},</strong></p>
  
  <p style="margin: 0 0 25px 0; font-size: 20px; font-weight: 500; line-height: 1.5;">${customMessage.trim() ? customMessage.trim() : `Thanks for your interest in our <strong>${vehicleInfo}</strong>. I've created a personalized vehicle package just for you with all the details, photos, and payment options.`}</p>
  
  <!-- Vehicle Image -->
  <div style="text-align: center; margin: 25px 0;">
    <a href="https://wiersauto.com/" target="_blank" style="text-decoration: none; display: inline-block;">
      <img src="${mainImage}" alt="${vehicleInfo}" style="width: 100%; max-width: 400px; height: auto; border-radius: 10px; box-shadow: 0 4px 15px rgba(0,0,0,0.2); transition: transform 0.3s ease; cursor: pointer;" onmouseover="this.style.transform='scale(1.05)'" onmouseout="this.style.transform='scale(1)'">
    </a>
  </div>

  <!-- Vehicle Details -->
  <div style="background: #f8fafc; padding: 20px; border-radius: 10px; margin: 25px 0;">
    <div style="display: flex; justify-content: space-between; margin-bottom: 10px;">
      <span style="font-weight: bold; color: #1e40af;">Vehicle:</span>
      <span>${vehicleInfo}</span>
    </div>
    <div style="display: flex; justify-content: space-between; margin-bottom: 10px;">
      <span style="font-weight: bold; color: #1e40af;">Price:</span>
      <span style="color: #dc2626; font-weight: bold; font-size: 18px;">${priceDisplay}</span>
    </div>
    <div style="display: flex; justify-content: space-between; margin-bottom: 10px;">
      <span style="font-weight: bold; color: #1e40af;">Stock #:</span>
      <span>${stockNumber}</span>
    </div>
    <div style="display: flex; justify-content: space-between;">
      <span style="font-weight: bold; color: #1e40af;">Mileage:</span>
      <span>${mileage} miles</span>
    </div>
  </div>
</div>

<!-- Call to Action Button -->
<div style="text-align: center; margin: 40px 0;">
  <a href="${fullUrl}" style="background: linear-gradient(135deg, #3b82f6, #1e40af); color: white; padding: 20px 50px; text-decoration: none; border-radius: 50px; font-weight: bold; font-size: 20px; display: inline-block; box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4); transition: all 0.3s ease; text-transform: uppercase;">
    Vehicle Details Link
  </a>
  <p style="margin-top: 15px; font-size: 14px; color: #6b7280;">Click the button above to see photos, specs, and payment calculator</p>
</div>

</body>
</html>`;
  };

  // Copy email template to clipboard
  const copyEmailTemplate = async () => {
    const emailTemplate = generateHTMLEmailTemplate();
    try {
      await navigator.clipboard.writeText(emailTemplate);
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy email template:', err);
    }
  };

  return (
    <>
      <div className="min-h-screen relative overflow-hidden">
        {/* Animated Space Background */}
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full"
          style={{
            background: 'linear-gradient(135deg, #0c0c0c 0%, #1a1a2e 25%, #16213e 50%, #0f3460 75%, #533483 100%)'
          }}
        />
        
        {/* Floating particles overlay */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 left-1/4 w-1 h-1 bg-purple-400 rounded-full animate-ping"></div>
          <div className="absolute top-3/4 right-1/4 w-0.5 h-0.5 bg-blue-400 rounded-full animate-pulse"></div>
          <div className="absolute top-1/2 left-1/6 w-1 h-1 bg-cyan-400 rounded-full animate-bounce"></div>
          <div className="absolute top-1/6 right-1/3 w-0.5 h-0.5 bg-purple-300 rounded-full animate-ping" style={{animationDelay: '1s'}}></div>
          <div className="absolute bottom-1/4 left-1/3 w-1 h-1 bg-indigo-400 rounded-full animate-pulse" style={{animationDelay: '2s'}}></div>
        </div>

        {/* Main Content */}
        <div className="relative z-10 min-h-screen flex flex-col items-center p-0">
        {/* Nav Bar + Accent Line (not fixed, scrolls away) */}
        <div
          className={`w-full flex flex-col items-center transition-transform duration-700 ease-out ${navVisible ? 'translate-y-0 opacity-100' : '-translate-y-20 opacity-0'}`}
          style={{ pointerEvents: navVisible ? 'auto' : 'none' }}
        >
          <div className="w-[95vw] max-w-6xl flex flex-row items-center px-8 py-4 gap-4 animate-slideDown"
            style={{
              background: 'rgba(255,255,255,0.7)',
              boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
              borderRadius: '2rem',
              border: '1.5px solid rgba(255,255,255,0.25)',
              backdropFilter: 'blur(18px)',
              WebkitBackdropFilter: 'blur(18px)',
              minHeight: 64,
              marginTop: 12,
              position: 'relative',
            }}
          >
            {/* Logo/Icon - Clickable to return to admin dashboard */}
            <button
              className="flex items-center justify-center mr-6 hover:scale-105 transition-transform duration-200"
              onClick={() => window.location.href = '/admin/dashboard'}
              title="Return to Admin Dashboard"
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-blue-400 flex items-center justify-center shadow-lg border-2 border-white hover:shadow-xl">
                <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="text-white"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0h6" /></svg>
              </div>
            </button>
            <button
              className="bg-white/80 text-blue-900 font-bold rounded-full px-5 py-2 shadow hover:bg-blue-100 transition mr-6 text-lg border border-blue-100"
              onClick={() => window.history.back()}
            >
              &larr; Back
            </button>
            {/* Desktop Navigation */}
            <div className="hidden sm:block vehicle-detail-main-nav overflow-x-auto whitespace-nowrap flex flex-row gap-1 sm:gap-2 relative" style={{ WebkitOverflowScrolling: 'touch' }}>
              {/* Right gradient scroll hint for mobile */}
              <div className="absolute right-0 top-0 h-full w-8 pointer-events-none block sm:hidden" style={{background: 'linear-gradient(to left, #fff 60%, transparent)'}}></div>
              {navLinks.map((link, idx) => {
                if (link.label === 'Inspection Forms') {
                  return (
                    <button
                      key={link.label}
                      className={`text-xs sm:text-base font-semibold px-2 sm:px-5 py-1.5 sm:py-2 rounded-full transition duration-200 ${activeTab === link.label ? 'bg-blue-500/90 text-white shadow' : 'bg-white/60 text-blue-900 hover:bg-blue-100 hover:text-blue-800'} hover:scale-105 hover:shadow-lg border border-blue-100`}
                      style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.01em' }}
                      onClick={() => setShowInspectionModal(true)}
                    >
                      {link.label}
                    </button>
                  );
                } else if (link.label === 'AutoCheck History') {
                  return (
                    <button
                      key={link.label}
                      className={`text-xs sm:text-base font-semibold px-2 sm:px-5 py-1.5 sm:py-2 rounded-full transition duration-200 ${activeTab === link.label ? 'bg-blue-500/90 text-white shadow' : 'bg-white/60 text-blue-900 hover:bg-blue-100 hover:text-blue-800'} hover:scale-105 hover:shadow-lg border border-blue-100`}
                      style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.01em' }}
                      onClick={() => setShowAutoCheckModal(true)}
                    >
                      {link.label}
                    </button>
                  );
                } else if (link.label === 'Original MSRP / Options') {
                  return (
                    <button
                      key={link.label}
                      className={`text-xs sm:text-base font-semibold px-2 sm:px-5 py-1.5 sm:py-2 rounded-full transition duration-200 ${activeTab === link.label ? 'bg-blue-500/90 text-white shadow' : 'bg-white/60 text-blue-900 hover:bg-blue-100 hover:text-blue-800'} hover:scale-105 hover:shadow-lg border border-blue-100`}
                      style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.01em' }}
                      onClick={() => setShowMsrpModal(true)}
                    >
                      {link.label}
                    </button>
                  );
                } else {
                  return (
                    <button
                      key={link.label}
                      className={`text-xs sm:text-base font-semibold px-2 sm:px-5 py-1.5 sm:py-2 rounded-full transition duration-200 ${activeTab === link.label ? 'bg-blue-500/90 text-white shadow' : 'bg-white/60 text-blue-900 hover:bg-blue-100 hover:text-blue-800'} hover:scale-105 hover:shadow-lg border border-blue-100`}
                      style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.01em' }}
                      onClick={() => setActiveTab(link.label)}
                    >
                      {link.label}
                    </button>
                  );
                }
              })}
            </div>

          </div>
          {/* Accent line */}
          <div className="w-full h-1 bg-gradient-to-r from-blue-400 via-blue-200 to-blue-400 opacity-60" style={{ borderRadius: 8, maxWidth: 1200, height: accentHeight }} />
        </div>

        {/* Mobile Card-Based Navigation */}
        <div className="w-full flex sm:hidden justify-center mb-4">
          <div className="w-full max-w-xs px-4 space-y-2">
            {navLinks.map((link) => {
              const isActive = activeTab === link.label;
              const isPdf = ['Inspection Forms', 'AutoCheck History', 'Original MSRP / Options'].includes(link.label);
              
              return (
                <button
                  key={link.label}
                  className={`w-full p-3 rounded-xl font-semibold text-left transition-all duration-200 ${
                    isActive && !isPdf 
                      ? 'bg-blue-500 text-white shadow-md' 
                      : 'bg-white/90 text-gray-800 border border-gray-200 hover:border-blue-300'
                  } backdrop-blur-sm text-sm`}
                  onClick={() => {
                    if (link.label === 'Inspection Forms') {
                      setShowInspectionModal(true);
                    } else if (link.label === 'AutoCheck History') {
                      setShowAutoCheckModal(true);
                    } else if (link.label === 'Original MSRP / Options') {
                      setShowMsrpModal(true);
                    } else {
                      setActiveTab(link.label);
                    }
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-lg mr-2">
                        {link.label === 'General Info.' && '🚗'}
                        {link.label === 'AutoCheck History' && '📋'}
                        {link.label === 'Inspection Forms' && '🔍'}
                        {link.label === 'Original MSRP / Options' && '💰'}
                      </span>
                      <span>{link.label}</span>
                    </div>
                    <span className="text-lg">
                      {isPdf ? '📄' : '→'}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main Content */}
        <div className="w-full flex flex-col items-center">
          {/* Mobile-Only Vehicle Info Card */}
          <div className="sm:hidden w-full max-w-lg px-4 mb-4">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-slate-700 to-slate-800 text-white p-4">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 rounded-full p-2">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h4a1 1 0 011 1v5m-6 0V9a1 1 0 011-1h4a1 1 0 011 1v11" />
                    </svg>
                  </div>
                  <div className="flex-1 text-center">
                    <div className="text-xl font-bold leading-tight">
                      {year} {make} {model}
                    </div>
                    {trim && <div className="text-slate-200 text-sm">{trim}</div>}
                  </div>
                </div>
                <div className="text-center mt-3">
                  <div className="flex items-center justify-center gap-4">
                    <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      Stock: {stockNumber}
                    </span>
                    <span className="bg-white/20 text-white px-3 py-1 rounded-full text-xs font-mono">
                      VIN: {vinField.slice(-6)}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Price Section */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 text-center">
                <div className="text-4xl font-extrabold text-green-600 mb-1">
                  {typeof price === 'number' ? `$${price.toLocaleString()}` : price}
                </div>
                <div className="text-green-700 font-semibold text-sm">
                  💰 Competitive Pricing Available
                </div>
              </div>
              
              {/* Vehicle Specs Grid */}
              <div className="p-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center bg-gray-50 rounded-xl p-3">
                    <div className="text-2xl mb-1">🏁</div>
                    <div className="text-xs text-gray-600 font-medium uppercase tracking-wide">Mileage</div>
                    <div className="font-bold text-gray-900">{mileage}</div>
                  </div>
                  <div className="text-center bg-gray-50 rounded-xl p-3">
                    <div className="text-2xl mb-1">🎨</div>
                    <div className="text-xs text-gray-600 font-medium uppercase tracking-wide">Exterior</div>
                    <div className="font-bold text-gray-900 text-sm leading-tight">{exteriorColor}</div>
                  </div>
                  <div className="text-center bg-gray-50 rounded-xl p-3">
                    <div className="text-2xl mb-1">🪑</div>
                    <div className="text-xs text-gray-600 font-medium uppercase tracking-wide">Interior</div>
                    <div className="font-bold text-gray-900 text-sm leading-tight">{interiorColor}</div>
                  </div>
                  <div className="text-center bg-gray-50 rounded-xl p-3">
                    <div className="text-2xl mb-1">⚙️</div>
                    <div className="text-xs text-gray-600 font-medium uppercase tracking-wide">Drivetrain</div>
                    <div className="font-bold text-gray-900 text-sm leading-tight">{drivetrain}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile-Only Main Photo */}
          <div className="sm:hidden w-full max-w-lg px-4 mb-4">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
              <div className="relative aspect-video bg-gradient-to-br from-gray-100 to-gray-200">
                <img
                  src={mainImage}
                  alt={`${year} ${make} ${model}`}
                  className="w-full h-full object-cover cursor-pointer"
                  loading="lazy"
                  decoding="async"
                  onClick={() => setPhotoModal({ open: true, index: 0 })}
                  onError={e => (e.target.src = 'https://via.placeholder.com/600x350?text=No+Image')}
                />
                <div className="absolute top-3 right-3 bg-black/70 text-white px-3 py-1.5 rounded-full text-sm font-semibold">
                  📸 {gallery.length || 1} Photos
                </div>
              </div>
            </div>
          </div>

          {/* Mobile-Only Gallery */}
          {gallery.length > 1 && (
            <div className="sm:hidden w-full max-w-lg px-4 mb-4">
              <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-3">
                  <div className="text-center">
                    <div className="text-lg font-bold">🖼️ Photo Gallery</div>
                    <div className="text-purple-200 text-sm">{gallery.length} photos available</div>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {gallery.map((url, idx) => (
                      <img
                        key={idx}
                        src={url}
                        alt={`Gallery ${idx + 1}`}
                        className="w-20 h-16 object-cover rounded-lg border border-gray-200 cursor-pointer hover:scale-105 transition-transform duration-200 flex-shrink-0"
                        loading="lazy"
                        decoding="async"
                        onClick={() => setPhotoModal({ open: true, index: idx })}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Mobile-Only Seller Videos */}
          {videoUrl && (
            <div className="sm:hidden w-full max-w-lg px-4 mb-4">
              <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-3">
                  <div className="text-center">
                    <div className="text-lg font-bold">🎥 Seller Videos</div>
                    <div className="text-red-200 text-sm">Detailed vehicle walkthrough</div>
                  </div>
                </div>
                <div className="aspect-video bg-black">
                  {videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be') ? (
                    <iframe
                      title="Seller Video"
                      src={
                        videoUrl.includes('watch?v=')
                          ? `https://www.youtube.com/embed/${videoUrl.split('watch?v=')[1].split('&')[0]}`
                          : videoUrl.includes('youtu.be/')
                          ? `https://www.youtube.com/embed/${videoUrl.split('youtu.be/')[1].split('?')[0]}`
                          : videoUrl
                      }
                      className="w-full h-full"
                      allowFullScreen
                    />
                  ) : videoUrl.includes('vimeo.com') ? (
                    <iframe
                      title="Seller Video"
                      src={
                        videoUrl.includes('player.vimeo.com')
                          ? videoUrl
                          : `https://player.vimeo.com/video/${videoUrl.split('vimeo.com/')[1].split(/[?/]/)[0]}`
                      }
                      className="w-full h-full"
                      allowFullScreen
                    />
                  ) : (
                    <video src={videoUrl} controls className="w-full h-full bg-black" />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Mobile-Only Quick Actions */}
          <div className="hidden w-full max-w-lg px-4 mb-4">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-3">
                <div className="text-center">
                  <div className="text-lg font-bold">🚗 Quick Actions</div>
                  <div className="text-blue-200 text-sm">Trade evaluation and financing</div>
                </div>
              </div>
              <div className="p-4 space-y-3">
                <button
                  onClick={() => {
                    const tradeInUrl = dealership 
                      ? `https://wierscalculator.com/trade-in/${dealership.name.split(' ')[0].toLowerCase()}`
                      : 'https://wierscalculator.com/trade-in/wiers';
                    window.open(tradeInUrl, '_blank');
                  }}
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-3.5 rounded-xl font-bold text-base shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-3"
                >
                  <span className="text-xl">💰</span>
                  <span>Vehicle Trade-In Evaluation</span>
                </button>
                <button
                  onClick={() => window.open('https://www.wiersauto.com/financing/application.htm', '_blank')}
                  className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-3.5 rounded-xl font-bold text-base shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-3"
                >
                  <span className="text-xl">📋</span>
                  <span>Finance & Lease Application</span>
                </button>
              </div>
            </div>
          </div>

          {/* Mobile-Only Vehicle Package Status Checklist */}
          <div className="hidden w-full max-w-lg px-4 mb-4">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-4">
                <div className="flex items-center gap-3 justify-center">
                  <div className="bg-white/20 rounded-full p-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="text-center">
                    <h3 className="text-lg font-bold">Vehicle Package Status</h3>
                    <p className="text-blue-100 text-sm">Ready-to-send checklist</p>
                  </div>
                </div>
              </div>
              <div className="p-4">
                <div className="space-y-3 mb-4">
                  {/* Photos */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${
                        gallery.length > 0 ? 'bg-green-500' : 'bg-red-500'
                      }`}></div>
                      <span className="text-sm font-medium text-gray-700">Photos</span>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                      gallery.length > 0 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {gallery.length > 0 ? '✓' : '✗'}
                    </div>
                  </div>

                  {/* Video */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${
                        videoUrl ? 'bg-green-500' : 'bg-red-500'
                      }`}></div>
                      <span className="text-sm font-medium text-gray-700">Video</span>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                      videoUrl 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {videoUrl ? '✓' : '✗'}
                    </div>
                  </div>

                  {/* AutoCheck */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${
                        autoCheckPdfUrl ? 'bg-green-500' : 'bg-red-500'
                      }`}></div>
                      <span className="text-sm font-medium text-gray-700">AutoCheck</span>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                      autoCheckPdfUrl 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {autoCheckPdfUrl ? '✓' : '✗'}
                    </div>
                  </div>

                  {/* Inspection */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${
                        inspectionPdfUrl ? 'bg-green-500' : 'bg-red-500'
                      }`}></div>
                      <span className="text-sm font-medium text-gray-700">Inspection</span>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                      inspectionPdfUrl 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {inspectionPdfUrl ? '✓' : '✗'}
                    </div>
                  </div>

                  {/* MSRP */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${
                        msrpPdfUrl ? 'bg-green-500' : 'bg-red-500'
                      }`}></div>
                      <span className="text-sm font-medium text-gray-700">MSRP</span>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                      msrpPdfUrl 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {msrpPdfUrl ? '✓' : '✗'}
                    </div>
                  </div>

                                   </div>

                 {/* Summary */}
                 <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                   <div className="text-sm font-medium text-blue-800 mb-1">Package Completeness</div>
                   <div className="text-xs text-blue-600">
                     {(() => {
                       const items = [
                         gallery.length > 0,
                         videoUrl,
                         autoCheckPdfUrl,
                         inspectionPdfUrl,
                         msrpPdfUrl
                       ];
                       const available = items.filter(Boolean).length;
                       const total = items.length;
                       const percentage = Math.round((available / total) * 100);
                       return `${available}/${total} items available (${percentage}%)`;
                     })()}
                   </div>
                 </div>
              </div>
            </div>
          </div>

          {/* Mobile-Only Calculator Section */}
          <div className="hidden w-full max-w-lg px-4 mb-4">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-indigo-600 to-purple-700 text-white p-4">
                <div className="text-center">
                  <div className="text-2xl mb-2">🧮</div>
                  <h2 className="text-xl font-bold">Payment Calculator</h2>
                  <p className="text-indigo-200 text-sm">Calculate monthly payments</p>
                </div>
              </div>
              
              <div className="p-4">
                {/* Payment Type Tabs */}
                <div className="flex gap-2 mb-6">
                  {['finance', 'cash'].map((type) => (
                    <button
                      key={type}
                      className={`flex-1 py-3 rounded-xl font-bold text-base border-2 transition-all duration-200 ${
                        paymentType === type 
                          ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white border-indigo-600 shadow-lg' 
                          : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100'
                      }`}
                      onClick={() => setPaymentType(type)}
                    >
                      {type === 'finance' ? '💳 Finance' : '💵 Cash'}
                    </button>
                  ))}
                </div>

                {paymentType === 'cash' ? (
                  /* Mobile Cash Payment View */
                  <div className="space-y-4">
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border-2 border-green-200 text-center">
                      <div className="text-sm text-gray-600 mb-2">Total Cash Price</div>
                      <div className="text-3xl font-bold text-green-600">
                        ${(calcVehiclePrice + (includeTaxes ? calcTaxes : 0) + calcFees - tradeInValue).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-2xl p-4">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-gray-900">Include Taxes & Fees</span>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={includeTaxes}
                            onChange={e => setIncludeTaxes(e.target.checked)}
                          />
                          <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-green-500 transition-colors duration-200"></div>
                          <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-200 peer-checked:translate-x-5 shadow"></div>
                        </label>
                      </div>
                    </div>

                    {includeTaxes && (
                      <div className="bg-blue-50 rounded-2xl p-4 border border-blue-200">
                        <div className="text-gray-700 font-semibold mb-2">📍 Zip Code for Tax Rate</div>
                        <div className="flex items-center gap-3">
                          <input
                            type="text"
                            className="flex-1 py-3 border-2 border-gray-300 rounded-xl px-4 text-center text-lg font-semibold focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                            value={zipCode}
                            placeholder="Enter zip code"
                            maxLength={5}
                            onChange={handleZipCodeChange}
                            style={{ fontSize: '16px' }}
                          />
                          {taxLoading && (
                            <div className="w-6 h-6 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin"></div>
                          )}
                        </div>
                        {zipCode.length === 5 && taxRate > 0 && (
                          <div className="text-sm text-green-700 mt-3 bg-green-100 rounded-lg p-3 text-center">
                            ✅ Tax Rate: {taxRate.toFixed(2)}% ({taxRegion}, {taxState})
                          </div>
                        )}
                        {zipCode.length === 5 && taxRate === 0 && !taxLoading && (
                          <div className="text-sm text-red-600 mt-3 bg-red-100 rounded-lg p-3 text-center">
                            ❌ Tax rate not found for this zip code
                          </div>
                        )}
                      </div>
                    )}

                    <div className="bg-gray-50 rounded-2xl p-4">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-gray-900">Trade-In Vehicle</span>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={showTradeIn}
                            onChange={() => setShowTradeIn(!showTradeIn)}
                          />
                          <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-orange-500 transition-colors duration-200"></div>
                          <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-200 peer-checked:translate-x-5 shadow"></div>
                        </label>
                      </div>
                    </div>

                    {showTradeIn && (
                      <div className="bg-white rounded-2xl p-4 border-2 border-orange-200">
                        <div className="text-gray-700 font-semibold mb-2">🚗 Your Trade-In Value</div>
                        <input
                          type="text"
                          className="w-full py-4 border-2 border-gray-300 rounded-xl px-4 text-center text-xl font-bold focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all"
                          value={tradeInValue === 0 ? '' : tradeInValue}
                          placeholder="$0"
                          onChange={e => {
                            let val = e.target.value;
                            if (val.length > 1) val = val.replace(/^0+/, '');
                            if (val === '') {
                              setTradeInValue('');
                            } else if (/^\d+$/.test(val)) {
                              setTradeInValue(Number(val));
                            }
                          }}
                          style={{ fontSize: '16px' }}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  /* Mobile Finance Payment View */
                  <div className="space-y-4">
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border-2 border-blue-200 text-center">
                      <div className="text-sm text-gray-600 mb-2">Estimated Monthly Payment</div>
                      <div className="text-4xl font-bold text-blue-600 mb-2">
                        ${isFinite(calcMonthlyPayment) ? calcMonthlyPayment.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '--'}
                      </div>
                      <div className="text-sm text-gray-500">per month for {calcTerm} months</div>
                    </div>

                    <div className="bg-white rounded-2xl p-4 border-2 border-gray-200">
                      <div className="text-gray-700 font-semibold mb-3">💰 Down Payment</div>
                      <input
                        type="number"
                        className="w-full py-4 border-2 border-gray-300 rounded-xl px-4 text-center text-xl font-bold focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                        placeholder="$0"
                        value={calcDownPayment}
                        min={0}
                        max={calcVehiclePrice}
                        onChange={e => {
                          const inputValue = e.target.value;
                          setCalcDownPayment(inputValue === '' ? '' : Number(inputValue));
                        }}
                        style={{ fontSize: '16px' }}
                      />
                      <div className="mt-3">
                        <input
                          type="range"
                          min={0}
                          max={calcVehiclePrice}
                          step={100}
                          value={calcDownPayment || 0}
                          onChange={e => setCalcDownPayment(Number(e.target.value))}
                          className="w-full h-3 accent-blue-600 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                        />
                      </div>
                    </div>

                    <div className="bg-white rounded-2xl p-4 border-2 border-gray-200">
                      <div className="text-gray-700 font-semibold mb-3">📅 Loan Term</div>
                      <div className="grid grid-cols-3 gap-2">
                        {[36, 48, 60, 72, 84].map((t) => (
                          <button
                            key={t}
                            className={`py-3 rounded-xl border-2 font-semibold transition-all duration-150 ${
                              calcTerm === t 
                                ? 'bg-blue-600 text-white border-blue-600 shadow-lg' 
                                : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100'
                            }`}
                            onClick={() => setCalcTerm(t)}
                          >
                            <div className="text-lg font-bold">{t}</div>
                            <div className="text-xs">months</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-4 border-2 border-purple-200">
                      <div className="text-gray-700 font-semibold mb-3">📊 Your Credit Score</div>
                      <div className="text-center mb-4">
                        <div className="text-3xl font-bold text-purple-600 mb-1">{calcCreditScore}</div>
                        <div className="text-sm text-gray-500">
                          {calcCreditScore >= 750 ? 'Excellent' : calcCreditScore >= 700 ? 'Good' : calcCreditScore >= 650 ? 'Fair' : 'Poor'}
                        </div>
                      </div>
                      <input 
                        type="range" 
                        min="500" 
                        max="850" 
                        step="10" 
                        value={calcCreditScore} 
                        onChange={e => setCalcCreditScore(Number(e.target.value))} 
                        className="w-full h-3 accent-purple-600 bg-gray-200 rounded-lg appearance-none cursor-pointer" 
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>500</span>
                        <span>850</span>
                      </div>
                    </div>

                    <div className="bg-yellow-50 rounded-2xl p-4 border-2 border-yellow-200">
                      <div className="text-gray-700 font-semibold mb-3">🎯 Interest Rate (APR)</div>
                      <div className="flex items-center justify-center gap-3">
                        <input
                          type="number"
                          value={calcApr}
                          onChange={(e) => setCalcApr(parseFloat(e.target.value))}
                          className="text-center py-4 px-4 border-2 border-gray-300 rounded-xl text-2xl font-bold w-32 focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 transition-all"
                          step="0.01"
                          style={{ fontSize: '16px' }}
                        />
                        <span className="text-2xl font-bold text-gray-800">%</span>
                      </div>
                      <div className="text-center text-sm text-gray-600 mt-2 bg-white rounded-lg py-2">
                        💡 Suggested: {getAprForCreditScore(calcCreditScore, 'finance')}% for your credit score
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-2xl p-4">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-gray-900">Include Taxes & Fees</span>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={includeTaxes}
                            onChange={e => setIncludeTaxes(e.target.checked)}
                          />
                          <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-green-500 transition-colors duration-200"></div>
                          <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-200 peer-checked:translate-x-5 shadow"></div>
                        </label>
                      </div>
                    </div>

                    {includeTaxes && (
                      <div className="bg-blue-50 rounded-2xl p-4 border border-blue-200">
                        <div className="text-gray-700 font-semibold mb-2">📍 Zip Code for Tax Rate</div>
                        <div className="flex items-center gap-3">
                          <input
                            type="text"
                            className="flex-1 py-3 border-2 border-gray-300 rounded-xl px-4 text-center text-lg font-semibold focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                            value={zipCode}
                            placeholder="Enter zip code"
                            maxLength={5}
                            onChange={handleZipCodeChange}
                            style={{ fontSize: '16px' }}
                          />
                          {taxLoading && (
                            <div className="w-6 h-6 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin"></div>
                          )}
                        </div>
                        {zipCode.length === 5 && taxRate > 0 && (
                          <div className="text-sm text-green-700 mt-3 bg-green-100 rounded-lg p-3 text-center">
                            ✅ Tax Rate: {taxRate.toFixed(2)}% ({taxRegion}, {taxState})
                          </div>
                        )}
                        {zipCode.length === 5 && taxRate === 0 && !taxLoading && (
                          <div className="text-sm text-red-600 mt-3 bg-red-100 rounded-lg p-3 text-center">
                            ❌ Tax rate not found for this zip code
                          </div>
                        )}
                      </div>
                    )}

                    <div className="bg-gray-50 rounded-2xl p-4">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-gray-900">Trade-In Vehicle</span>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={showTradeIn}
                            onChange={() => setShowTradeIn(!showTradeIn)}
                          />
                          <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-orange-500 transition-colors duration-200"></div>
                          <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-200 peer-checked:translate-x-5 shadow"></div>
                        </label>
                      </div>
                    </div>

                    {showTradeIn && (
                      <div className="bg-white rounded-2xl p-4 border-2 border-orange-200">
                        <div className="text-gray-700 font-semibold mb-2">🚗 Your Trade-In Value</div>
                        <input
                          type="text"
                          className="w-full py-4 border-2 border-gray-300 rounded-xl px-4 text-center text-xl font-bold focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all"
                          value={tradeInValue === 0 ? '' : tradeInValue}
                          placeholder="$0"
                          onChange={e => {
                            let val = e.target.value;
                            if (val.length > 1) val = val.replace(/^0+/, '');
                            if (val === '') {
                              setTradeInValue('');
                            } else if (/^\d+$/.test(val)) {
                              setTradeInValue(Number(val));
                            }
                          }}
                          style={{ fontSize: '16px' }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile-Only Send Packet Section */}
          <div className="sm:hidden w-full max-w-lg px-4 mb-6">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white p-4">
                <div className="text-center">
                  <div className="text-2xl mb-2">🚀</div>
                  <h2 className="text-xl font-bold">Share Vehicle</h2>
                  <p className="text-green-200 text-sm">Send personalized vehicle packet</p>
                </div>
              </div>
              <div className="p-4">
                <button
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-6 py-4 rounded-2xl font-bold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-green-300"
                  onClick={openSendPacketModal}
                >
                  <div className="flex items-center justify-center gap-3">
                    <span className="text-2xl">🚀</span>
                    <span>Send Vehicle Packet</span>
                  </div>
                </button>
                <p className="text-center text-gray-600 text-sm mt-3">
                  Create a personalized link to share vehicle details, photos, and payment calculator
                </p>
              </div>
            </div>
          </div>

          {/* Desktop Layout - Unchanged */}
          <div className="vehicle-detail-main w-full max-w-7xl bg-white rounded-3xl shadow-2xl flex flex-row overflow-hidden border border-gray-200 mt-2">
            {/* Gallery & Main Photo - Enhanced */}
            <div className="w-1/2 flex flex-col items-center p-6 lg:p-8 bg-gradient-to-br from-gray-50 to-white">
              {/* Main Image with Modern Frame */}
              <div className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl border-4 border-white overflow-hidden mb-6">
                <div className="relative aspect-video bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                <img
                  src={mainImage}
                    alt={`${year} ${make} ${model}`}
                    className="main-image w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                  decoding="async"
                  onClick={() => setPhotoModal({ open: true, index: 0 })}
                  onError={e => (e.target.src = 'https://via.placeholder.com/600x350?text=No+Image')}
                />
                  {/* Photo Badge */}
                  <div className="absolute top-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    📸 {gallery.length || 1} Photos
              </div>
                </div>
              </div>

              {/* Enhanced Gallery Thumbnails */}
              {gallery.length > 1 && (
                <div className="w-full max-w-2xl mb-6">
                  <div className="bg-white rounded-2xl p-4 shadow-lg border border-gray-200">
                    <div className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <span>🖼️</span>
                      <span>Gallery ({gallery.length} photos)</span>
                    </div>
                    <div className="gallery-thumbnails flex gap-3 overflow-x-auto pb-2">
                  {gallery.map((url, idx) => (
                    <img
                      key={idx}
                      src={url}
                      alt={`Gallery ${idx + 1}`}
                          className="w-20 h-16 object-cover rounded-xl border-2 border-gray-200 cursor-pointer hover:scale-110 hover:shadow-lg transition-all duration-200 flex-shrink-0"
                      loading="lazy"
                      decoding="async"
                      onClick={() => setPhotoModal({ open: true, index: idx })}
                    />
                  ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Enhanced Seller Videos */}
              {videoUrl && (
                <div className="w-full max-w-2xl mb-6">
                  <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                    <div className="bg-gradient-to-r from-red-500 to-pink-600 text-white p-4">
                      <div className="flex items-center gap-3">
                        <div className="bg-white/20 rounded-full p-2">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-5.5-2h3m-3 4h3" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-bold text-lg">🎥 Seller Videos</h3>
                          <p className="text-red-100 text-sm">Detailed vehicle walkthrough</p>
                        </div>
                      </div>
                    </div>
                    <div className="aspect-video bg-black flex items-center justify-center">
                    {videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be') ? (
                      <iframe
                        title="Seller Video"
                        src={
                          videoUrl.includes('watch?v=')
                            ? `https://www.youtube.com/embed/${videoUrl.split('watch?v=')[1].split('&')[0]}`
                            : videoUrl.includes('youtu.be/')
                            ? `https://www.youtube.com/embed/${videoUrl.split('youtu.be/')[1].split('?')[0]}`
                            : videoUrl
                        }
                        className="w-full h-full"
                        allowFullScreen
                      />
                    ) : videoUrl.includes('vimeo.com') ? (
                      <iframe
                        title="Seller Video"
                        src={
                          videoUrl.includes('player.vimeo.com')
                            ? videoUrl
                            : `https://player.vimeo.com/video/${videoUrl.split('vimeo.com/')[1].split(/[?/]/)[0]}`
                        }
                        className="w-full h-full"
                        allowFullScreen
                      />
                    ) : (
                      <video src={videoUrl} controls className="w-full h-full bg-black" />
                    )}
                    </div>
                  </div>
                </div>
              )}

              {/* Enhanced Action Buttons */}
              <div className="w-full max-w-2xl space-y-4">
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white p-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-white/20 rounded-full p-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-bold text-lg">🚗 Quick Actions</h3>
                        <p className="text-blue-100 text-sm">Trade evaluation and financing</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 space-y-3">
                  <button
                    onClick={() => {
                      const tradeInUrl = dealership 
                        ? `https://wierscalculator.com/trade-in/${dealership.name.split(' ')[0].toLowerCase()}`
                        : 'https://wierscalculator.com/trade-in/wiers';
                      window.open(tradeInUrl, '_blank');
                    }}
                      className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-3 hover:scale-105"
                  >
                      <span>Vehicle Trade-In Evaluation</span>
                  </button>
                  <button
                    onClick={() => window.open('https://www.wiersauto.com/financing/application.htm', '_blank')}
                      className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-3 hover:scale-105"
                  >
                      <span>Finance & Lease Application</span>
                  </button>
                </div>
              </div>
            </div>
            </div>
            {/* Enhanced Vehicle Info */}
            <div className="w-1/2 flex flex-col gap-6 p-6 lg:p-8 bg-gradient-to-bl from-blue-50 to-white">
              
              {/* Vehicle Package Status Checklist - ACTUAL RIGHT SIDE */}
              <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-white/20 rounded-full p-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold">Vehicle Package Status</h3>
                      <p className="text-blue-100 text-sm">Ready-to-send checklist</p>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {/* Photos */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${
                          gallery.length > 0 ? 'bg-green-500' : 'bg-red-500'
                        }`}></div>
                        <span className="text-sm font-medium text-gray-700">Photos</span>
                      </div>
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                        gallery.length > 0 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {gallery.length > 0 ? '✓' : '✗'}
                      </div>
                    </div>

                    {/* Video */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${
                          videoUrl ? 'bg-green-500' : 'bg-red-500'
                        }`}></div>
                        <span className="text-sm font-medium text-gray-700">Video</span>
                      </div>
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                        videoUrl 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {videoUrl ? '✓' : '✗'}
                      </div>
                    </div>

                    {/* AutoCheck */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${
                          autoCheckPdfUrl ? 'bg-green-500' : 'bg-red-500'
                        }`}></div>
                        <span className="text-sm font-medium text-gray-700">AutoCheck</span>
                      </div>
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                        autoCheckPdfUrl 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {autoCheckPdfUrl ? '✓' : '✗'}
                      </div>
                    </div>

                    {/* Inspection */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${
                          inspectionPdfUrl ? 'bg-green-500' : 'bg-red-500'
                        }`}></div>
                        <span className="text-sm font-medium text-gray-700">Inspection</span>
                      </div>
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                        inspectionPdfUrl 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {inspectionPdfUrl ? '✓' : '✗'}
                      </div>
                    </div>

                    {/* MSRP */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${
                          msrpPdfUrl ? 'bg-green-500' : 'bg-red-500'
                        }`}></div>
                        <span className="text-sm font-medium text-gray-700">MSRP</span>
                      </div>
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                        msrpPdfUrl 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {msrpPdfUrl ? '✓' : '✗'}
                      </div>
                    </div>

                  </div>

                  {/* Summary */}
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="text-sm font-medium text-blue-800 mb-1">Package Completeness</div>
                    <div className="text-xs text-blue-600">
                      {(() => {
                        const items = [
                          gallery.length > 0,
                          videoUrl,
                          autoCheckPdfUrl,
                          inspectionPdfUrl,
                          msrpPdfUrl
                        ];
                        const available = items.filter(Boolean).length;
                        const total = items.length;
                        const percentage = Math.round((available / total) * 100);
                        return `${available}/${total} items available (${percentage}%)`;
                      })()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Main Vehicle Info Card */}
              <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">
                {/* Vehicle Header */}
                <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white p-6">
                  <div className="flex items-center gap-4">
                    <div className="bg-white/20 rounded-full p-3">
                      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h4a1 1 0 011 1v5m-6 0V9a1 1 0 011-1h4a1 1 0 011 1v11" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h1 className="text-3xl font-bold leading-tight mb-2">
                  {year} {make} {model} {trim}
                      </h1>
                      <div className="flex items-center gap-4">
                        <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                          Stock: {stockNumber}
                        </span>
                        <span className="bg-white/20 text-white px-3 py-1 rounded-full text-sm font-mono">
                          VIN: {vinField.slice(-6)}
                        </span>
                </div>
                </div>
                </div>
                </div>

                {/* Price Section */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 border-b">
                  <div className="text-center">
                    <div className="text-6xl font-extrabold text-green-600 mb-2 drop-shadow-sm">
                  {typeof price === 'number' ? `$${price.toLocaleString()}` : price}
                </div>
                    <div className="text-green-700 font-semibold text-lg">
                      💰 Competitive Pricing Available
                  </div>
                  </div>
                  </div>

                {/* Vehicle Specifications */}
                <div className="p-6">
                  <div className="mb-4">
                    <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                      <span>📋</span>
                      <span>Vehicle Specifications</span>
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                      <div className="text-gray-600 text-sm font-medium mb-1"> Mileage</div>
                      <div className="font-bold text-gray-900 text-lg">{mileage} miles</div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                      <div className="text-gray-600 text-sm font-medium mb-1"> Exterior</div>
                      <div className="font-bold text-gray-900 text-lg">{exteriorColor}</div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                      <div className="text-gray-600 text-sm font-medium mb-1"> Interior</div>
                      <div className="font-bold text-gray-900 text-lg">{interiorColor}</div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                      <div className="text-gray-600 text-sm font-medium mb-1"> Body Type</div>
                      <div className="font-bold text-gray-900 text-lg">{body}</div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 sm:col-span-2">
                      <div className="text-gray-600 text-sm font-medium mb-1"> Drivetrain</div>
                      <div className="font-bold text-gray-900 text-lg">{drivetrain}</div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Enhanced Payment Calculator */}
              <div className="mt-6">
                <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
                  {/* Calculator Header */}
                  <div className="bg-gradient-to-r from-indigo-600 to-purple-700 text-white p-4">
                    <div className="text-center">
                      <div className="text-2xl mb-2"></div>
                      <h2 className="text-xl font-bold">Payment Calculator</h2>
                      <p className="text-indigo-100 text-sm">Calculate monthly payments</p>
                    </div>
                  </div>

                  <div className="p-4">
                    {/* Payment Type Tabs */}
                    <div className="flex gap-2 mb-6">
                      {['finance', 'cash'].map((type) => (
                        <button
                          key={type}
                          className={`flex-1 py-3 rounded-xl font-bold text-base border-2 transition-all duration-200 ${
                            paymentType === type 
                              ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white border-indigo-600 shadow-lg' 
                              : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100'
                          }`}
                          onClick={() => setPaymentType(type)}
                        >
                          {type === 'finance' ? '💳 Finance' : '💵 Cash'}
                        </button>
                      ))}
                    </div>

                    {paymentType === 'cash' ? (
                      /* Cash Payment View */
                      <div className="space-y-4">
                        {/* Total Cash Price Display */}
                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border-2 border-green-200 text-center">
                          <div className="text-sm text-gray-600 mb-2">Total Cash Price</div>
                          <div className="text-3xl font-bold text-green-600 mb-4">
                            ${(calcVehiclePrice + (includeTaxes ? calcTaxes : 0) + calcFees - tradeInValue).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                          </div>
                        </div>

                        {/* Include Taxes Toggle */}
                        <div className="bg-gray-50 rounded-2xl p-4">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-gray-900">Include Taxes & Fees</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={includeTaxes}
                                onChange={e => setIncludeTaxes(e.target.checked)}
                              />
                              <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-green-500 transition-colors duration-200"></div>
                              <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-200 peer-checked:translate-x-5 shadow"></div>
                            </label>
                          </div>
                        </div>

                        {/* Zip Code for Taxes */}
                        {includeTaxes && (
                          <div className="bg-blue-50 rounded-2xl p-4 border border-blue-200">
                            <div className="text-gray-700 font-semibold mb-2">📍 Zip Code for Tax Rate</div>
                            <div className="flex items-center gap-3">
                              <input
                                type="text"
                                className="flex-1 py-3 border-2 border-gray-300 rounded-xl px-4 text-center text-lg font-semibold focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                                value={zipCode}
                                placeholder="Enter zip code"
                                maxLength={5}
                                onChange={handleZipCodeChange}
                              />
                              {taxLoading && (
                                <div className="w-6 h-6 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin"></div>
                              )}
                            </div>
                            {zipCode.length === 5 && taxRate > 0 && (
                              <div className="text-sm text-green-700 mt-3 bg-green-100 rounded-lg p-3 text-center">
                                ✅ Tax Rate: {taxRate.toFixed(2)}% ({taxRegion}, {taxState})
                              </div>
                            )}
                            {zipCode.length === 5 && taxRate === 0 && !taxLoading && (
                              <div className="text-sm text-red-600 mt-3 bg-red-100 rounded-lg p-3 text-center">
                                ❌ Tax rate not found for this zip code
                              </div>
                            )}
                          </div>
                        )}

                        {/* Trade-In Toggle */}
                        <div className="bg-gray-50 rounded-2xl p-4">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-gray-900">Trade-In Vehicle</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={showTradeIn}
                                onChange={() => setShowTradeIn(!showTradeIn)}
                              />
                              <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-orange-500 transition-colors duration-200"></div>
                              <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-200 peer-checked:translate-x-5 shadow"></div>
                            </label>
                          </div>
                        </div>

                        {/* Trade-In Value Input */}
                        {showTradeIn && (
                          <div className="bg-white rounded-2xl p-4 border-2 border-orange-200">
                            <div className="text-gray-700 font-semibold mb-2">🚗 Your Trade-In Value</div>
                            <input
                              type="text"
                              className="w-full py-4 border-2 border-gray-300 rounded-xl px-4 text-center text-xl font-bold focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all"
                              value={tradeInValue === 0 ? '' : tradeInValue}
                              placeholder="$0"
                              min={0}
                              onChange={e => {
                                let val = e.target.value;
                                if (val.length > 1) val = val.replace(/^0+/, '');
                                if (val === '') {
                                  setTradeInValue('');
                                } else if (/^\d+$/.test(val)) {
                                  setTradeInValue(Number(val));
                                }
                              }}
                            />
                          </div>
                        )}

                        {/* Cash Payment Breakdown */}
                        <div className="bg-gray-50 rounded-2xl p-4 space-y-3">
                          <h3 className="font-bold text-gray-800 text-center mb-3">💵 Payment Breakdown</h3>
                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">Vehicle Price:</span>
                              <span className="font-semibold">${calcVehiclePrice.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">Doc Fee:</span>
                              <span className="font-semibold">${calcFees.toLocaleString()}</span>
                            </div>
                            {includeTaxes && (
                              <div className="flex justify-between items-center">
                                <span className="text-gray-600">Taxes:</span>
                                <span className="font-semibold">${calcTaxes.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                              </div>
                            )}
                            {showTradeIn && tradeInValue > 0 && (
                              <div className="flex justify-between items-center text-green-600">
                                <span className="text-gray-600">Trade-In Credit:</span>
                                <span className="font-semibold">-${tradeInValue.toLocaleString()}</span>
                              </div>
                            )}
                            <div className="border-t-2 border-gray-300 pt-2 mt-3">
                              <div className="flex justify-between items-center bg-white rounded-xl p-3 shadow-sm">
                                <span className="font-bold text-gray-800 text-lg">Total:</span>
                                <span className="text-2xl font-bold text-green-600">
                                  ${(calcVehiclePrice + (includeTaxes ? calcTaxes : 0) + calcFees - tradeInValue).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* Finance Payment View */
                      <div className="space-y-4">
                        {/* Monthly Payment Display */}
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border-2 border-blue-200 text-center">
                          <div className="text-sm text-gray-600 mb-2">Estimated Monthly Payment</div>
                          <div className="text-4xl font-bold text-blue-600 mb-2">
                            ${isFinite(calcMonthlyPayment) ? calcMonthlyPayment.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '--'}
                          </div>
                          <div className="text-sm text-gray-500">per month for {calcTerm} months</div>
                        </div>

                        {/* Down Payment */}
                        <div className="bg-white rounded-2xl p-4 border-2 border-gray-200">
                          <div className="text-gray-700 font-semibold mb-3">💰 Down Payment</div>
                          <input
                            type="number"
                            className="w-full py-4 border-2 border-gray-300 rounded-xl px-4 text-center text-xl font-bold focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                            placeholder="$0"
                            value={calcDownPayment}
                            min={0}
                            max={calcVehiclePrice}
                            onChange={e => {
                              const inputValue = e.target.value;
                              setCalcDownPayment(inputValue === '' ? '' : Number(inputValue));
                            }}
                          />
                          <div className="mt-3">
                            <input
                              type="range"
                              min={0}
                              max={calcVehiclePrice}
                              step={100}
                              value={calcDownPayment || 0}
                              onChange={e => setCalcDownPayment(Number(e.target.value))}
                              className="w-full h-3 accent-blue-600 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                            />
                          </div>
                        </div>

                        {/* Loan Term */}
                        <div className="bg-white rounded-2xl p-4 border-2 border-gray-200">
                          <div className="text-gray-700 font-semibold mb-3"> Loan Term</div>
                          <div className="grid grid-cols-3 gap-2">
                            {[36, 48, 60, 72, 84].map((t) => (
                              <button
                                key={t}
                                className={`py-3 rounded-xl border-2 font-semibold transition-all duration-150 ${calcTerm === t ? 'bg-blue-600 text-white border-blue-600 shadow-lg' : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100'}`}
                                onClick={() => setCalcTerm(t)}
                              >
                                <div className="text-lg font-bold">{t}</div>
                                <div className="text-xs">months</div>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Credit Score */}
                        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-4 border-2 border-purple-200">
                          <div className="text-gray-700 font-semibold mb-3"> Your Credit Score</div>
                          <div className="text-center mb-4">
                            <div className="text-3xl font-bold text-purple-600 mb-1">{calcCreditScore}</div>
                            <div className="text-sm text-gray-500">
                              {calcCreditScore >= 750 ? 'Excellent' : calcCreditScore >= 700 ? 'Good' : calcCreditScore >= 650 ? 'Fair' : 'Poor'}
                            </div>
                          </div>
                          <input 
                            type="range" 
                            min="500" 
                            max="850" 
                            step="10" 
                            value={calcCreditScore} 
                            onChange={e => setCalcCreditScore(Number(e.target.value))} 
                            className="w-full h-3 accent-purple-600 bg-gray-200 rounded-lg appearance-none cursor-pointer" 
                          />
                          <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>500</span>
                            <span>850</span>
                          </div>
                        </div>

                        {/* APR */}
                        <div className="bg-yellow-50 rounded-2xl p-4 border-2 border-yellow-200">
                          <div className="text-gray-700 font-semibold mb-3"> Interest Rate (APR)</div>
                          <div className="flex items-center justify-center gap-3">
                            <input
                              type="number"
                              value={calcApr}
                              onChange={(e) => setCalcApr(parseFloat(e.target.value))}
                              className="text-center py-4 px-4 border-2 border-gray-300 rounded-xl text-2xl font-bold w-32 focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 transition-all"
                              step="0.01"
                            />
                            <span className="text-2xl font-bold text-gray-800">%</span>
                          </div>
                          <div className="text-center text-sm text-gray-600 mt-2 bg-white rounded-lg py-2">
                            💡 Suggested: {getAprForCreditScore(calcCreditScore, 'finance')}% for your credit score
                          </div>
                        </div>

                        {/* Include Taxes Toggle */}
                        <div className="bg-gray-50 rounded-2xl p-4">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-gray-900">Include Taxes & Fees</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={includeTaxes}
                                onChange={e => setIncludeTaxes(e.target.checked)}
                              />
                              <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-green-500 transition-colors duration-200"></div>
                              <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-200 peer-checked:translate-x-5 shadow"></div>
                            </label>
                          </div>
                        </div>

                        {/* Zip Code for Taxes */}
                        {includeTaxes && (
                          <div className="bg-blue-50 rounded-2xl p-4 border border-blue-200">
                            <div className="text-gray-700 font-semibold mb-2">📍 Zip Code for Tax Rate</div>
                            <div className="flex items-center gap-3">
                              <input
                                type="text"
                                className="flex-1 py-3 border-2 border-gray-300 rounded-xl px-4 text-center text-lg font-semibold focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                                value={zipCode}
                                placeholder="Enter zip code"
                                maxLength={5}
                                onChange={handleZipCodeChange}
                              />
                              {taxLoading && (
                                <div className="w-6 h-6 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin"></div>
                              )}
                            </div>
                            {zipCode.length === 5 && taxRate > 0 && (
                              <div className="text-sm text-green-700 mt-3 bg-green-100 rounded-lg p-3 text-center">
                                ✅ Tax Rate: {taxRate.toFixed(2)}% ({taxRegion}, {taxState})
                              </div>
                            )}
                            {zipCode.length === 5 && taxRate === 0 && !taxLoading && (
                              <div className="text-sm text-red-600 mt-3 bg-red-100 rounded-lg p-3 text-center">
                                ❌ Tax rate not found for this zip code
                              </div>
                            )}
                          </div>
                        )}

                        {/* Trade-In Toggle */}
                        <div className="bg-gray-50 rounded-2xl p-4">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-gray-900">Trade-In Vehicle</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={showTradeIn}
                                onChange={() => setShowTradeIn(!showTradeIn)}
                              />
                              <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-orange-500 transition-colors duration-200"></div>
                              <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-200 peer-checked:translate-x-5 shadow"></div>
                            </label>
                          </div>
                        </div>

                        {/* Trade-In Value Input */}
                        {showTradeIn && (
                          <div className="bg-white rounded-2xl p-4 border-2 border-orange-200">
                            <div className="text-gray-700 font-semibold mb-2"> Your Trade-In Value</div>
                            <input
                              type="text"
                              className="w-full py-4 border-2 border-gray-300 rounded-xl px-4 text-center text-xl font-bold focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all"
                              value={tradeInValue === 0 ? '' : tradeInValue}
                              placeholder="$0"
                              min={0}
                              onChange={e => {
                                let val = e.target.value;
                                if (val.length > 1) val = val.replace(/^0+/, '');
                                if (val === '') {
                                  setTradeInValue('');
                                } else if (/^\d+$/.test(val)) {
                                  setTradeInValue(Number(val));
                                }
                              }}
                            />
                          </div>
                        )}

                        {/* Payment Summary */}
                        <div className="bg-gray-50 rounded-2xl p-4 space-y-3">
                          <h3 className="font-bold text-gray-800 text-center mb-3"> Payment Summary</h3>
                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">Term:</span>
                              <span className="font-semibold">{calcTerm} months</span>
                            </div>
                            {calcDownPayment && calcDownPayment > 0 && (
                              <div className="flex justify-between items-center">
                                <span className="text-gray-600">Down Payment:</span>
                                <span className="font-semibold">${Number(calcDownPayment).toLocaleString()}</span>
                              </div>
                            )}
                            {showTradeIn && tradeInValue > 0 && (
                              <div className="flex justify-between items-center text-green-600">
                                <span className="text-gray-600">Trade-In Credit:</span>
                                <span className="font-semibold">-${tradeInValue.toLocaleString()}</span>
                              </div>
                            )}
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">APR:</span>
                              <span className="font-semibold">{calcApr.toFixed(2)}%</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">Doc Fee:</span>
                              <span className="font-semibold">${calcFees.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                            </div>
                            {includeTaxes && (
                              <div className="flex justify-between items-center">
                                <span className="text-gray-600">Taxes:</span>
                                <span className="font-semibold">${calcTaxes.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                              </div>
                            )}
                            <div className="border-t-2 border-gray-300 pt-2 mt-3">
                              <div className="flex justify-between items-center bg-white rounded-xl p-3 shadow-sm">
                                <span className="font-bold text-gray-800 text-lg">Monthly Payment:</span>
                                <span className="text-2xl font-bold text-blue-600">
                                  ${isFinite(calcMonthlyPayment) ? calcMonthlyPayment.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '--'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* Inspection Forms Tab Content */}
              {showInspectionModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
                  <div className="pdf-modal bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-6xl p-0 sm:p-6 relative flex flex-col items-center overflow-y-auto max-h-screen sm:max-h-[90vh]">
                    <button
                      className="absolute top-2 right-2 text-black bg-white rounded-full text-4xl sm:text-2xl font-bold z-20 p-3 sm:p-1.5 shadow focus:outline-none focus:ring-2 focus:ring-blue-400"
                      style={{ minWidth: 52, minHeight: 52 }}
                      onClick={() => setShowInspectionModal(false)}
                      aria-label="Close"
                    >
                      &times;
                    </button>
                    <div className="font-bold text-gray-800 mb-2 sm:mb-4 text-base sm:text-lg">Inspection Forms</div>
                    {inspectionPdfUrl ? (
                      <>
                        {/* Mobile: Better PDF experience */}
                        <div className="block sm:hidden w-full px-4">
                          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
                            <div className="text-6xl mb-4"></div>
                            <div className="text-lg font-semibold text-blue-900 mb-3">Inspection Forms PDF</div>
                            <div className="text-sm text-gray-600 mb-6">
                              For the best mobile viewing experience, please open the PDF in a new tab or download it to your device.
                            </div>
                            <div className="space-y-3">
                              <a
                                href={inspectionPdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block w-full bg-blue-600 text-white font-semibold rounded-lg px-4 py-4 text-base shadow hover:bg-blue-700 transition"
                              >
                                 Open in New Tab
                              </a>
                              <a
                                href={inspectionPdfUrl}
                                download
                                className="block w-full bg-green-600 text-white font-semibold rounded-lg px-4 py-4 text-base shadow hover:bg-green-700 transition"
                              >
                                 Download PDF
                              </a>
                            </div>
                            <div className="text-xs text-gray-500 mt-4">
                              💡 Tip: After opening, you can zoom and scroll normally in your browser
                            </div>
                          </div>
                        </div>
                        
                        {/* Desktop: Keep iframe */}
                        <div className="hidden sm:block w-full">
                          <iframe
                            src={inspectionPdfUrl}
                            title="Inspection Forms PDF"
                            className="w-full h-[85vh] rounded-xl border shadow"
                            style={{ minHeight: '60vh' }}
                          />
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-64 flex items-center justify-center bg-gray-100 rounded-xl text-gray-500 text-lg shadow-inner">
                        No inspection PDF uploaded for this vehicle.
                      </div>
                    )}
                  </div>
                </div>
              )}
              {/* AutoCheck History Modal */}
              {showAutoCheckModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
                  <div className="pdf-modal bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-6xl p-2 sm:p-6 relative flex flex-col items-center overflow-y-auto max-h-screen sm:max-h-[90vh]">
                    <button
                      className="absolute top-2 right-2 text-black bg-white rounded-full text-3xl sm:text-2xl font-bold z-20 p-2 sm:p-1.5 shadow focus:outline-none focus:ring-2 focus:ring-blue-400"
                      style={{ minWidth: 44, minHeight: 44 }}
                      onClick={() => setShowAutoCheckModal(false)}
                      aria-label="Close"
                    >
                      &times;
                    </button>
                    <div className="font-bold text-gray-800 mb-4 text-lg">AutoCheck History</div>
                    {autoCheckPdfUrl ? (
                      <>
                        {/* Mobile: Better PDF experience */}
                        <div className="block sm:hidden w-full px-4">
                          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
                            <div className="text-6xl mb-4"></div>
                            <div className="text-lg font-semibold text-blue-900 mb-3">AutoCheck History PDF</div>
                            <div className="text-sm text-gray-600 mb-6">
                              For the best mobile viewing experience, please open the PDF in a new tab or download it to your device.
                            </div>
                            <div className="space-y-3">
                              <a
                                href={autoCheckPdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block w-full bg-blue-600 text-white font-semibold rounded-lg px-4 py-4 text-base shadow hover:bg-blue-700 transition"
                              >
                                 Open in New Tab
                              </a>
                              <a
                                href={autoCheckPdfUrl}
                                download
                                className="block w-full bg-green-600 text-white font-semibold rounded-lg px-4 py-4 text-base shadow hover:bg-green-700 transition"
                              >
                                 Download PDF
                              </a>
                            </div>
                            <div className="text-xs text-gray-500 mt-4">
                              💡 Tip: After opening, you can zoom and scroll normally in your browser
                            </div>
                          </div>
                        </div>
                        
                        {/* Desktop: Keep iframe */}
                        <div className="hidden sm:block w-full">
                          <iframe
                            src={autoCheckPdfUrl}
                            title="AutoCheck PDF"
                            className="w-full h-[85vh] rounded-xl border shadow"
                            style={{ minHeight: '60vh' }}
                          />
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-64 flex items-center justify-center bg-gray-100 rounded-xl text-gray-500 text-lg shadow-inner">
                        No AutoCheck PDF uploaded for this vehicle.
                      </div>
                    )}
                  </div>
                </div>
              )}
              {/* Original MSRP / Options Modal */}
              {showMsrpModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
                  <div className="pdf-modal bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-6xl p-2 sm:p-6 relative flex flex-col items-center overflow-y-auto max-h-screen sm:max-h-[90vh]">
                    <button
                      className="absolute top-2 right-2 text-black bg-white rounded-full text-3xl sm:text-2xl font-bold z-20 p-2 sm:p-1.5 shadow focus:outline-none focus:ring-2 focus:ring-blue-400"
                      style={{ minWidth: 44, minHeight: 44 }}
                      onClick={() => setShowMsrpModal(false)}
                      aria-label="Close"
                    >
                      &times;
                    </button>
                    <div className="font-bold text-gray-800 mb-4 text-lg">Original MSRP / Options</div>
                    
                    {msrpPdfUrl ? (
                      <>
                        {/* Mobile: Better PDF experience */}
                        <div className="block sm:hidden w-full px-4">
                          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
                            <div className="text-6xl mb-4"></div>
                            <div className="text-lg font-semibold text-blue-900 mb-3">Original MSRP / Options PDF</div>
                            <div className="text-sm text-gray-600 mb-6">
                              For the best mobile viewing experience, please open the PDF in a new tab or download it to your device.
                            </div>
                            <div className="space-y-3">
                              <a
                                href={msrpPdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block w-full bg-blue-600 text-white font-semibold rounded-lg px-4 py-4 text-base shadow hover:bg-blue-700 transition"
                              >
                                 Open in New Tab
                              </a>
                              <a
                                href={msrpPdfUrl}
                                download
                                className="block w-full bg-green-600 text-white font-semibold rounded-lg px-4 py-4 text-base shadow hover:bg-green-700 transition"
                              >
                                 Download PDF
                              </a>
                            </div>
                            <div className="text-xs text-gray-500 mt-4">
                              💡 Tip: After opening, you can zoom and scroll normally in your browser
                            </div>
                          </div>
                        </div>
                        
                        {/* Desktop: Keep iframe */}
                        <div className="hidden sm:block w-full">
                          <iframe
                            src={msrpPdfUrl}
                            title="MSRP PDF"
                            className="w-full h-[85vh] rounded-xl border shadow"
                            style={{ minHeight: '60vh' }}
                          />
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-64 flex items-center justify-center bg-gray-100 rounded-xl text-gray-500 text-lg shadow-inner">
                        No MSRP PDF uploaded for this vehicle.
                      </div>
                    )}
                  </div>
                </div>
              )}
              {/* Action Buttons */}
              <div className="mt-6 sm:mt-8">
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white p-4 sm:p-6">
                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className="bg-white/20 rounded-full p-2 sm:p-3">
                        <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl sm:text-2xl font-bold">📧 Share Vehicle</h3>
                        <p className="text-green-100 text-sm sm:text-base">Send personalized vehicle packet to customers</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 sm:p-6">
                    <button
                      className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-6 py-4 sm:py-5 rounded-2xl font-bold text-lg sm:text-xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-green-300"
                      onClick={openSendPacketModal}
                    >
                      <div className="flex items-center justify-center gap-3">
                        <span className="text-2xl">🚀</span>
                        <span>Send Vehicle Packet</span>
                      </div>
                    </button>
                    <p className="text-center text-gray-600 text-sm mt-3">
                      Create a personalized link to share vehicle details, photos, and payment calculator
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Animation keyframes for nav bar */}
      <style>{`
        @keyframes slideDown {
          0% { transform: translateY(-80px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
        .animate-slideDown {
          animation: slideDown 0.7s cubic-bezier(0.4,0,0.2,1) forwards;
        }
      `}</style>
      {showSendPacketModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-60 flex items-center justify-center p-0 md:p-4">
          <div className="send-packet-modal bg-white w-full h-full md:w-full md:h-auto md:max-w-2xl md:rounded-2xl shadow-2xl md:p-6 relative overflow-y-auto md:max-h-screen">
            
            {/* Mobile Header - Full Width Gradient Header */}
            <div className="md:hidden sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 shadow-lg z-20">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-xl font-bold">📧 Send Vehicle Packet</h1>
                  <p className="text-blue-100 text-sm">Create and share vehicle link</p>
                </div>
            <button
                  className="bg-white/20 hover:bg-white/30 rounded-full p-2 transition-colors"
                  onClick={() => {
                    setShowSendPacketModal(false);
                    setPastedCustomerText('');
                    setNewCustomer({ firstName: '', lastName: '', email: '', phone: '' });
                    setSelectedCustomer(null);
                    setCustomerSearch('');
                    setCustomerResults([]);
                    setLastGeneratedLink('');
                    setCustomMessage('');
                    setSelectedTemplate('default');
                  }}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Desktop Close Button */}
            <button
              className="hidden md:block absolute top-3 right-3 text-black bg-white rounded-full text-2xl font-bold z-20 p-1.5 shadow focus:outline-none focus:ring-2 focus:ring-blue-400"
              style={{ minWidth: 36, minHeight: 36 }}
              onClick={() => {
                setShowSendPacketModal(false);
                setPastedCustomerText('');
                setNewCustomer({ firstName: '', lastName: '', email: '', phone: '' });
                setSelectedCustomer(null);
                setCustomerSearch('');
                setCustomerResults([]);
                setLastGeneratedLink('');
                setCustomMessage('');
                setSelectedTemplate('default');
              }}
              aria-label="Close"
            >
              &times;
            </button>

            {/* Mobile Vehicle Card */}
            <div className="md:hidden bg-gray-50 p-4 border-b">
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="flex gap-3">
                  <img 
                    src={mainImage || 'https://via.placeholder.com/150x100?text=No+Image'} 
                    alt={`${year} ${make} ${model}`}
                    className="w-20 h-16 object-cover rounded-lg border flex-shrink-0" 
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-gray-900 text-base leading-tight">
                      {year} {make} {model}
                    </div>
                    <div className="text-green-600 font-bold text-lg">
                      {typeof price === 'number' ? `$${price.toLocaleString()}` : price}
                    </div>
                    <div className="flex gap-2 text-xs text-gray-500 mt-1">
                      <span className="bg-gray-100 px-2 py-0.5 rounded">Stock: {stockNumber}</span>
                      <span className="bg-gray-100 px-2 py-0.5 rounded">{mileage} mi</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Desktop Vehicle Summary */}
            <div className="hidden md:block mb-6">
              <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl p-6 border-2 border-gray-200 shadow-lg">
                <div className="flex gap-6">
                  <img 
                    src={mainImage} 
                    alt={`${year} ${make} ${model}`}
                    className="w-40 h-32 object-cover rounded-xl border-2 border-white shadow-md flex-shrink-0" 
                  />
                  <div className="flex-1">
                    <div className="text-2xl font-bold text-gray-900 mb-2 leading-tight">
                      {year} {make} {model} {trim}
                    </div>
                    <div className="text-green-600 font-bold text-2xl mb-3">
                      {typeof price === 'number' ? `$${price.toLocaleString()}` : price}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-white px-3 py-2 rounded-lg border border-gray-200 shadow-sm">
                        <div className="text-xs text-gray-500 font-medium">VIN</div>
                        <div className="font-mono text-sm text-gray-900">{vinField}</div>
                      </div>
                      <div className="bg-white px-3 py-2 rounded-lg border border-gray-200 shadow-sm">
                        <div className="text-xs text-gray-500 font-medium">Stock #</div>
                        <div className="font-bold text-sm text-gray-900">{stockNumber}</div>
                      </div>
                      <div className="bg-white px-3 py-2 rounded-lg border border-gray-200 shadow-sm">
                        <div className="text-xs text-gray-500 font-medium">Mileage</div>
                        <div className="font-bold text-sm text-gray-900">{mileage} miles</div>
                      </div>
                      <div className="bg-white px-3 py-2 rounded-lg border border-gray-200 shadow-sm">
                        <div className="text-xs text-gray-500 font-medium">Status</div>
                        <div className="font-bold text-sm text-green-600">Available</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Link Generation Section */}
            <div className="md:hidden bg-white p-4">
              <div className="bg-white rounded-2xl border-2 border-gray-200 overflow-hidden shadow-lg">
                {/* Mobile Link Header */}
                <div className="bg-gradient-to-r from-green-500 to-blue-600 text-white p-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-white/20 rounded-full p-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.102m0 0l4-4a4 4 0 105.656-5.656l-4 4a4 4 0 01-5.656 0z" />
                      </svg>
                    </div>
              <div>
                      <h3 className="font-bold text-lg">Shareable Web Link</h3>
                      <p className="text-green-100 text-sm">Generate and copy vehicle link</p>
                    </div>
              </div>
            </div>

                {/* Mobile Link Content */}
                <div className="p-4">
                  {lastGeneratedLink && selectedCustomer ? (
                    <div className="space-y-4">
                      <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
                        <div className="text-center mb-3">
                          <div className="text-green-700 font-bold text-lg">✅ Link Ready!</div>
                          <div className="text-green-600 text-sm">Share this link with {selectedCustomer.firstName}</div>
                        </div>
                        <div className="bg-white rounded-lg p-3 border mb-3">
                  <input
                    type="text"
                            className="w-full text-sm text-gray-700 bg-transparent border-none outline-none"
                    value={lastGeneratedLink}
                    readOnly
                    onFocus={e => e.target.select()}
                            style={{ fontSize: '14px' }}
                  />
                </div>
                <button
                          className="w-full bg-green-600 text-white py-3 px-4 rounded-xl font-bold shadow-lg hover:bg-green-700 transition-all flex items-center justify-center gap-2 mb-3"
                  onClick={async () => {
                    await navigator.clipboard.writeText(lastGeneratedLink);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                        >
                          {copied ? (
                            <>
                              <span className="text-lg">✅</span>
                              <span>Copied!</span>
                            </>
                          ) : (
                            <>
                              <span className="text-lg">📋</span>
                              <span>Copy Link</span>
                            </>
                          )}
                        </button>
                        <button
                          className="w-full bg-blue-600 text-white py-3 px-4 rounded-xl font-bold shadow-lg hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
                          onClick={() => setShowEmailTemplate(true)}
                        >
                          <span className="text-lg">📧</span>
                          <span>Create Email Template</span>
                </button>
              </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-gray-400 text-4xl mb-2">👤</div>
                      <div className="text-gray-600 font-medium">Select a customer first</div>
                      <div className="text-gray-500 text-sm">Choose from existing customers or add new one below</div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Template Selection */}
            <div className="mb-6">
              <div className="bg-white rounded-2xl border-2 border-gray-200 overflow-hidden shadow-xl">
                {/* Template Header */}
                <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white p-4 md:p-6">
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="bg-white/20 rounded-full p-2 md:p-3">
                      <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-lg md:text-xl">📋 Packet Template</h3>
                      <p className="text-orange-100 text-sm">Choose what the customer will see</p>
                    </div>
                  </div>
                </div>

                {/* Template Content */}
                <div className="p-4 md:p-6">
                  <div className="space-y-3">
                    {availableTemplates.map(template => (
                      <div 
                        key={template.id}
                        className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${
                          selectedTemplate === template.id 
                            ? 'border-orange-500 bg-orange-50' 
                            : 'border-gray-200 hover:border-orange-300 hover:bg-orange-25'
                        }`}
                        onClick={() => setSelectedTemplate(template.id)}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                            selectedTemplate === template.id 
                              ? 'border-orange-500 bg-orange-500' 
                              : 'border-gray-300'
                          }`}>
                            {selectedTemplate === template.id && (
                              <div className="w-2 h-2 bg-white rounded-full"></div>
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="font-bold text-gray-900 mb-1">{template.name}</div>
                            <div className="text-sm text-gray-600">{template.description}</div>
                            {template.id === 'cash-only' && (
                              <div className="mt-2 text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded-md inline-block">
                                🚫 Financing options will be hidden
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Desktop Link Generation */}
            <div className="hidden md:block mb-6">
              <div className="bg-white rounded-2xl border-2 border-gray-200 overflow-hidden shadow-xl">
                {/* Desktop Link Header */}
                <div className="bg-gradient-to-r from-green-500 to-blue-600 text-white p-6">
                  <div className="flex items-center gap-4">
                    <div className="bg-white/20 rounded-full p-3">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.102m0 0l4-4a4 4 0 105.656-5.656l-4 4a4 4 0 01-5.656 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-xl">🔗 Shareable Web Link</h3>
                      <p className="text-green-100 text-sm">Generate and copy vehicle link for customers</p>
                    </div>
                  </div>
                </div>

                {/* Desktop Link Content */}
                <div className="p-6">
                  {lastGeneratedLink && selectedCustomer ? (
                    <div className="space-y-4">
                      <div className="bg-green-50 border-2 border-green-200 rounded-xl p-5">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <div className="text-green-700 font-bold text-lg flex items-center gap-2">
                              <span>✅</span>
                              <span>Link Ready for {selectedCustomer.firstName}!</span>
                            </div>
                            <div className="text-green-600 text-sm">Share this personalized vehicle link</div>
                          </div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border-2 border-green-300 mb-4">
                          <input
                            type="text"
                            className="w-full text-gray-700 bg-transparent border-none outline-none text-base"
                            value={lastGeneratedLink}
                            readOnly
                            onFocus={e => e.target.select()}
                          />
                        </div>
                        <div className="flex gap-3">
                  <button
                            className="flex-1 bg-green-600 text-white py-3 px-6 rounded-xl font-bold shadow-lg hover:bg-green-700 transition-all flex items-center justify-center gap-2"
                            onClick={async () => {
                              await navigator.clipboard.writeText(lastGeneratedLink);
                              setCopied(true);
                              setTimeout(() => setCopied(false), 2000);
                            }}
                            type="button"
                          >
                            {copied ? (
                              <>
                                <span className="text-lg">✅</span>
                                <span>Copied!</span>
                              </>
                            ) : (
                              <>
                                <span className="text-lg">📋</span>
                                <span>Copy Link</span>
                              </>
                            )}
                          </button>
                          <button
                            className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-xl font-bold shadow-lg hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
                    onClick={() => setShowEmailTemplate(true)}
                    type="button"
                  >
                            <span className="text-lg">📧</span>
                            <span>Create Email Template</span>
                  </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <div className="text-gray-400 text-5xl mb-4">👤</div>
                      <div className="text-gray-600 font-semibold text-lg mb-2">Select a customer first</div>
                      <div className="text-gray-500">Choose from existing customers or add a new one below</div>
                </div>
              )}
                </div>
              </div>
            </div>

            {/* Mobile Customer Section */}
            <div className="md:hidden bg-white p-4 border-t">
              <div className="bg-white rounded-2xl border-2 border-gray-200 overflow-hidden shadow-lg">
                {/* Mobile Customer Header */}
                <div className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white p-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-white/20 rounded-full p-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">Customer Selection</h3>
                      <p className="text-purple-100 text-sm">Search existing or add new customer</p>
                    </div>
                  </div>
                </div>

                {/* Mobile Customer Content */}
                <div className="p-4">
                  {customerAdded && (
                    <div className="w-full bg-green-500 text-white text-center py-3 px-4 rounded-xl mb-4 font-bold shadow-lg animate-pulse">
                      ✅ Customer Added Successfully!
            </div>
                  )}

                  {!selectedCustomer ? (
                    <div className="space-y-4">
                      {/* Mobile Search */}
                      <div>
                        <div className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                          <span>🔍</span>
                          <span>Search Existing Customers</span>
                        </div>
                        <div className="relative">
                          <input
                            type="text"
                            className="w-full border-2 border-gray-300 rounded-xl px-4 py-4 pl-12 text-gray-900 bg-white focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
                            placeholder="Search by name, email, or phone..."
                            value={customerSearch}
                            onChange={e => setCustomerSearch(e.target.value)}
                            style={{ fontSize: '16px' }}
                          />
                          <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </div>
                      </div>

                      {/* Mobile Loading */}
                      {customerSearchLoading && (
                        <div className="flex items-center justify-center py-6 text-purple-600">
                          <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin mr-3"></div>
                          <span className="font-medium">Searching customers...</span>
                        </div>
                      )}

                      {/* Mobile Search Results */}
                      {customerResults.length > 0 && (
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          <div className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                            <span>📋</span>
                            <span>Found {customerResults.length} customer(s)</span>
                          </div>
                          {customerResults.map(c => (
                            <div 
                              className="bg-gray-50 rounded-xl p-4 border-2 border-transparent hover:border-purple-300 cursor-pointer transition-all shadow-sm" 
                              key={c._id || c.email}
                              onClick={() => { setSelectedCustomer(c); setCustomerSearch(''); setCustomerResults([]); }}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="font-semibold text-gray-900 text-base">
                                    {c.firstName || ''} {c.lastName || ''}
                                  </div>
                                  <div className="text-sm text-gray-600 break-all mt-1">
                                    {c.email || c.phone || 'No contact info'}
                                  </div>
                                </div>
                                <div className="ml-3 flex items-center gap-2">
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      if (window.confirm('Are you sure you want to delete this customer?')) {
                                        let res;
                                        
                                        const userRole = localStorage.getItem('adminRole');
                                        const userEmail = localStorage.getItem('adminEmail');
                                        const userName = localStorage.getItem('adminName');
                                        const userId = localStorage.getItem('adminId');
                                        const userParams = new URLSearchParams({
                                          userRole: userRole || 'admin',
                                          userEmail: userEmail || 'admin@system.com',
                                          userName: userName || 'Admin',
                                          userId: userId || 'system'
                                        });
                                        
                                        if (c._id && dealershipId) {
                                          res = await fetch(`/api/dealerships/${dealershipId}/customers/${c._id}?${userParams}`, { method: 'DELETE' });
                                          if (res.status === 404 && c.leadId) {
                                            res = await fetch(`/api/dealerships/${dealershipId}/leads/${c.leadId}`, { method: 'DELETE' });
                                          }
                                        } else if (c.leadId && dealershipId) {
                                          res = await fetch(`/api/dealerships/${dealershipId}/leads/${c.leadId}`, { method: 'DELETE' });
                                        }
                                        if (res && res.ok) {
                                          setCustomerResults(customerResults.filter(customer => customer._id !== c._id && customer.leadId !== c.leadId));
                                        } else {
                                          alert('Failed to delete customer.');
                                        }
                                      }
                                    }}
                                    className="p-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition"
                                  >
                                    🗑️
                                  </button>
                                  <div className="text-purple-500 text-xl">→</div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Mobile Add New Customer */}
                      <div className="border-t-2 border-gray-200 pt-4">
                        <div className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <span>➕</span>
                          <span>Add New Customer</span>
                        </div>
                        
                        {/* Mobile Quick Paste */}
                        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-3 mb-4">
                          <div className="text-xs font-semibold text-blue-800 mb-2 flex items-center gap-2">
                            <span>📋</span>
                            <span>Quick Paste & Parse</span>
                          </div>
                          <textarea
                            className="w-full border border-blue-300 rounded-lg px-3 py-2 text-xs bg-white"
                            placeholder="John Smith (555) 123-4567 john@email.com"
                            rows="2"
                            value={pastedCustomerText}
                            onChange={(e) => {
                              setPastedCustomerText(e.target.value);
                              if (e.target.value.trim()) {
                                parseCustomerText(e.target.value);
                              }
                            }}
                            style={{ fontSize: '14px' }}
                          />
                          <div className="flex gap-2 mt-2">
                            <button
                              type="button"
                              className="flex-1 px-3 py-1 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700 transition font-semibold"
                              onClick={() => parseCustomerText(pastedCustomerText)}
                            >
                              Parse
                            </button>
                            <button
                              type="button"
                              className="flex-1 px-3 py-1 bg-gray-400 text-white rounded-lg text-xs hover:bg-gray-500 transition"
                              onClick={() => {
                                setPastedCustomerText('');
                                setNewCustomer({ firstName: '', lastName: '', email: '', phone: '' });
                              }}
                            >
                              Clear
                            </button>
                          </div>
                        </div>

                        {/* Mobile Add Customer Form */}
                        <form onSubmit={handleAddCustomer} className="space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <input
                              type="text"
                              className="w-full border-2 border-gray-300 rounded-xl px-4 py-3 text-gray-900 bg-white focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all"
                              placeholder="First Name"
                              value={newCustomer.firstName}
                              onChange={e => setNewCustomer({ ...newCustomer, firstName: e.target.value })}
                              required
                              style={{ fontSize: '16px' }}
                            />
                            <input
                              type="text"
                              className="w-full border-2 border-gray-300 rounded-xl px-4 py-3 text-gray-900 bg-white focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all"
                              placeholder="Last Name"
                              value={newCustomer.lastName}
                              onChange={e => setNewCustomer({ ...newCustomer, lastName: e.target.value })}
                              required
                              style={{ fontSize: '16px' }}
                            />
                          </div>
                          <input
                            type="email"
                            className="w-full border-2 border-gray-300 rounded-xl px-4 py-3 text-gray-900 bg-white focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all"
                            placeholder="Email Address"
                            value={newCustomer.email}
                            onChange={e => setNewCustomer({ ...newCustomer, email: e.target.value })}
                            style={{ fontSize: '16px' }}
                          />
                          <input
                            type="text"
                            className="w-full border-2 border-gray-300 rounded-xl px-4 py-3 text-gray-900 bg-white focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all"
                            placeholder="Mobile Number"
                            value={newCustomer.phone}
                            onChange={e => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                            style={{ fontSize: '16px' }}
                          />
                          <button
                            type="submit"
                            className="w-full bg-gradient-to-r from-green-500 to-teal-600 text-white py-4 px-6 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-3"
                          >
                            <span className="text-xl">✅</span>
                            <span>Add New Customer</span>
                          </button>
                        </form>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200 rounded-xl p-4">
                      <div className="text-center mb-3">
                        <div className="text-green-700 font-bold text-lg flex items-center justify-center gap-2">
                          <span>✅</span>
                          <span>Customer Selected</span>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-3 mb-3">
                        <div className="font-semibold text-gray-900 text-base">
                          {selectedCustomer.firstName} {selectedCustomer.lastName}
                        </div>
                        <div className="text-sm text-gray-600 break-all mt-1">
                          {selectedCustomer.email} {selectedCustomer.phone && `• ${selectedCustomer.phone}`}
                        </div>
                      </div>
                      <button
                        className="w-full bg-blue-100 hover:bg-blue-200 text-blue-700 py-3 px-4 rounded-xl font-bold transition"
                        onClick={() => setSelectedCustomer(null)}
                        type="button"
                      >
                        🔄 Change Customer
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Desktop Customer Section */}
            <div className="hidden md:block mb-6">
              <div className="bg-white rounded-2xl border-2 border-gray-200 overflow-hidden shadow-xl">
                {/* Desktop Customer Header */}
                <div className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white p-6">
                  <div className="flex items-center gap-4">
                    <div className="bg-white/20 rounded-full p-3">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-xl">👤 Customer Selection</h3>
                      <p className="text-purple-100 text-sm">Search existing customers or add new ones</p>
                    </div>
                  </div>
                </div>

                {/* Desktop Customer Content */}
                <div className="p-6">
              {customerAdded && (
                    <div className="w-full bg-green-500 text-white text-center py-3 px-4 rounded-xl mb-6 font-bold shadow-lg animate-pulse">
                      ✅ Customer Added Successfully!
                    </div>
              )}

              {!selectedCustomer ? (
                    <div className="space-y-6">
                      {/* Desktop Search Section */}
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-5 border-2 border-blue-200">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="bg-blue-500 rounded-full p-2">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </div>
                          <div>
                            <div className="font-bold text-gray-900 text-lg">🔍 Search Existing Customers</div>
                            <div className="text-blue-600 text-sm">Find customers by name, email, or phone</div>
                          </div>
                        </div>
                        <div className="relative">
                  <input
                    type="text"
                            className="w-full border-2 border-blue-300 rounded-xl px-5 py-4 pl-12 text-gray-900 bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-base"
                    placeholder="Search by name, email, or phone..."
                    value={customerSearch}
                    onChange={e => setCustomerSearch(e.target.value)}
                  />
                          <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </div>

                        {/* Desktop Loading */}
                        {customerSearchLoading && (
                          <div className="flex items-center justify-center py-6 text-blue-600 mt-4">
                            <div className="w-6 h-6 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin mr-3"></div>
                            <span className="font-medium text-lg">Searching customers...</span>
                          </div>
                        )}

                        {/* Desktop Search Results */}
                  {customerResults.length > 0 && (
                          <div className="mt-4 space-y-3 max-h-60 overflow-y-auto">
                            <div className="font-semibold text-gray-700 text-base flex items-center gap-2">
                              <span>📋</span>
                              <span>Found {customerResults.length} customer(s)</span>
                            </div>
                      {customerResults.map(c => (
                              <div 
                                className="bg-white rounded-xl p-4 border-2 border-transparent hover:border-blue-300 cursor-pointer transition-all shadow-sm hover:shadow-md" 
                                key={c._id || c.email}
                                onClick={() => { setSelectedCustomer(c); setCustomerSearch(''); setCustomerResults([]); }}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <div className="font-semibold text-gray-900 text-lg">
                                      {c.firstName || ''} {c.lastName || ''}
                                    </div>
                                    <div className="text-gray-600 break-all mt-1">
                                      {c.email || c.phone || 'No contact info'}
                                    </div>
                                  </div>
                                  <div className="ml-4 flex items-center gap-3">
                          <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                              if (window.confirm('Are you sure you want to delete this customer?')) {
                                let res;
                                
                                const userRole = localStorage.getItem('adminRole');
                                const userEmail = localStorage.getItem('adminEmail');
                                const userName = localStorage.getItem('adminName');
                                const userId = localStorage.getItem('adminId');
                                const userParams = new URLSearchParams({
                                  userRole: userRole || 'admin',
                                  userEmail: userEmail || 'admin@system.com',
                                  userName: userName || 'Admin',
                                  userId: userId || 'system'
                                });
                                
                                if (c._id && dealershipId) {
                                  res = await fetch(`/api/dealerships/${dealershipId}/customers/${c._id}?${userParams}`, { method: 'DELETE' });
                                  if (res.status === 404 && c.leadId) {
                                    res = await fetch(`/api/dealerships/${dealershipId}/leads/${c.leadId}`, { method: 'DELETE' });
                                  }
                                } else if (c.leadId && dealershipId) {
                                  res = await fetch(`/api/dealerships/${dealershipId}/leads/${c.leadId}`, { method: 'DELETE' });
                                }
                                if (res && res.ok) {
                                  setCustomerResults(customerResults.filter(customer => customer._id !== c._id && customer.leadId !== c.leadId));
                                } else {
                                  alert('Failed to delete customer.');
                                }
                              }
                            }}
                                      className="p-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition"
                          >
                                      🗑️
                          </button>
                                    <div className="text-blue-500 text-xl">→</div>
                                  </div>
                                </div>
                        </div>
                      ))}
                    </div>
                  )}
                      </div>

                      {/* Desktop Add New Customer Section */}
                      <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-2xl p-5 border-2 border-green-200">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="bg-green-500 rounded-full p-2">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                          </div>
                          <div>
                            <div className="font-bold text-gray-900 text-lg">➕ Add New Customer</div>
                            <div className="text-green-600 text-sm">Create a new customer record</div>
                          </div>
                        </div>

                        {/* Desktop Quick Paste */}
                        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-4">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-blue-600 text-lg">📋</span>
                            <span className="font-semibold text-blue-800">Quick Paste & Parse</span>
                          </div>
                          <div className="text-sm text-blue-600 mb-3">
                            Paste customer info (name, phone, email) and it will auto-fill the form below
                          </div>
                    <textarea
                            className="w-full border border-blue-300 rounded-lg px-4 py-3 text-sm bg-white"
                      placeholder={`Example:
John Smith
(Individual) 
C: (555) 123-4567

john.smith@email.com
Chicago, IL, 60601`}
                      rows="4"
                      value={pastedCustomerText}
                      onChange={(e) => {
                        setPastedCustomerText(e.target.value);
                        if (e.target.value.trim()) {
                          parseCustomerText(e.target.value);
                        }
                      }}
                    />
                          <div className="flex gap-3 mt-3">
                      <button
                        type="button"
                              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
                        onClick={() => parseCustomerText(pastedCustomerText)}
                      >
                              📝 Parse Info
                      </button>
                      <button
                        type="button"
                              className="flex-1 px-4 py-2 bg-gray-400 text-white rounded-lg font-semibold hover:bg-gray-500 transition"
                        onClick={() => {
                          setPastedCustomerText('');
                          setNewCustomer({ firstName: '', lastName: '', email: '', phone: '' });
                        }}
                      >
                              🗑️ Clear All
                      </button>
                    </div>
                  </div>

                        {/* Desktop Add Customer Form */}
                        <form onSubmit={handleAddCustomer} className="bg-white rounded-xl p-5 border-2 border-green-300 shadow-sm">
                          <div className="grid grid-cols-2 gap-4 mb-4">
                      <input
                        type="text"
                              className="w-full border-2 border-gray-300 rounded-xl px-4 py-3 text-gray-900 bg-white focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all"
                        placeholder="First Name"
                        value={newCustomer.firstName}
                        onChange={e => setNewCustomer({ ...newCustomer, firstName: e.target.value })}
                        required
                      />
                      <input
                        type="text"
                              className="w-full border-2 border-gray-300 rounded-xl px-4 py-3 text-gray-900 bg-white focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all"
                        placeholder="Last Name"
                        value={newCustomer.lastName}
                        onChange={e => setNewCustomer({ ...newCustomer, lastName: e.target.value })}
                        required
                      />
                    </div>
                    <input
                      type="email"
                            className="w-full border-2 border-gray-300 rounded-xl px-4 py-3 text-gray-900 bg-white focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all mb-4"
                            placeholder="Email Address"
                      value={newCustomer.email}
                      onChange={e => setNewCustomer({ ...newCustomer, email: e.target.value })}
                    />
                    <input
                      type="text"
                            className="w-full border-2 border-gray-300 rounded-xl px-4 py-3 text-gray-900 bg-white focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all mb-4"
                      placeholder="Mobile Number"
                      value={newCustomer.phone}
                      onChange={e => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                    />
                    <button
                      type="submit"
                            className="w-full bg-gradient-to-r from-green-500 to-teal-600 text-white py-4 px-6 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-3"
                    >
                            <span className="text-xl">✅</span>
                            <span>Add New Customer</span>
                    </button>
                  </form>
                      </div>
                    </div>
              ) : (
                    <div className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200 rounded-xl p-6">
                      <div className="text-center mb-4">
                        <div className="text-green-700 font-bold text-xl flex items-center justify-center gap-3 mb-2">
                          <span>✅</span>
                          <span>Customer Selected</span>
                        </div>
                        <div className="text-green-600">Ready to generate link for this customer</div>
                      </div>
                      <div className="bg-white rounded-xl p-5 mb-4 border-2 border-green-300">
                        <div className="font-semibold text-gray-900 text-lg mb-2">
                          {selectedCustomer.firstName} {selectedCustomer.lastName}
                        </div>
                        <div className="text-gray-600 break-all">
                          {selectedCustomer.email} {selectedCustomer.phone && `• ${selectedCustomer.phone}`}
                        </div>
                      </div>
                  <button
                        className="w-full bg-blue-100 hover:bg-blue-200 text-blue-700 py-3 px-6 rounded-xl font-bold transition flex items-center justify-center gap-2"
                    onClick={() => setSelectedCustomer(null)}
                    type="button"
                  >
                        <span>🔄</span>
                        <span>Change Customer</span>
                  </button>
                </div>
              )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {photoModal.open && gallery[photoModal.index] && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80">
          <div className="image-modal bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-3xl p-6 relative flex flex-col items-center">
            <button
              className="absolute top-2 right-2 text-black bg-white border border-black rounded-full text-4xl font-bold z-20 p-2 shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
              style={{ minWidth: 48, minHeight: 48 }}
              onClick={() => setPhotoModal({ open: false, index: 0 })}
              aria-label="Close"
            >
              &times;
            </button>
            {/* Pinch-to-zoom for mobile */}
            {window.innerWidth < 640 ? (
              <PinchZoomImage src={gallery[photoModal.index]} alt={`Vehicle Photo ${photoModal.index + 1}`} />
            ) : (
              <div className="flex items-center justify-center w-[900px] max-w-full h-[75vh] max-h-[85vh] bg-white rounded-xl shadow-lg">
                <img
                  src={gallery[photoModal.index]}
                  alt={`Vehicle Photo ${photoModal.index + 1}`}
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            )}
            <div className="flex gap-4 mt-4 items-center justify-center w-[900px] max-w-full">
              <button
                className="px-4 py-2 bg-white/80 rounded shadow text-lg font-bold"
                onClick={() => setPhotoModal(v => ({ open: true, index: (v.index - 1 + gallery.length) % gallery.length }))}
              >
                &larr;
              </button>
              <span className="text-gray-800 font-semibold min-w-[70px] text-center">Photo {photoModal.index + 1} of {gallery.length}</span>
              <button
                className="px-4 py-2 bg-white/80 rounded shadow text-lg font-bold"
                onClick={() => setPhotoModal(v => ({ open: true, index: (v.index + 1) % gallery.length }))}
              >
                &rarr;
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Template Modal */}
      {showEmailTemplate && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-0 md:p-4">
          <div className="bg-white w-full h-full md:w-full md:h-auto md:max-w-4xl md:rounded-2xl shadow-2xl border-0 md:border md:border-gray-200 md:p-6 relative overflow-y-auto md:max-h-[90vh] md:m-4">
            
            {/* Mobile Header - Full Width Gradient Header */}
            <div className="md:hidden sticky top-0 bg-gradient-to-r from-indigo-600 to-blue-600 text-white p-4 shadow-lg z-20">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-xl font-bold">📧 Email Template</h1>
                  <p className="text-indigo-100 text-sm">Professional email with clickable button</p>
                </div>
            <button
                  className="bg-white/20 hover:bg-white/30 rounded-full p-2 transition-colors"
                  onClick={() => setShowEmailTemplate(false)}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Desktop Close Button */}
            <button
              className="hidden md:block absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl font-bold"
              onClick={() => setShowEmailTemplate(false)}
              aria-label="Close"
            >
              ×
            </button>
            
            {/* Desktop Header */}
            <div className="hidden md:block mb-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">📧 Email Template</h2>
              <p className="text-gray-600">Professional email template with clickable "View Vehicle" button</p>
            </div>

            {/* Mobile Custom Message Section */}
            <div className="md:hidden bg-white p-4 border-b">
              <div className="bg-white rounded-2xl border-2 border-blue-200 overflow-hidden shadow-lg">
                {/* Mobile Custom Message Header */}
                <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-white/20 rounded-full p-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
              </div>
                    <div>
                      <h3 className="font-bold text-lg">✏️ Custom Message</h3>
                      <p className="text-blue-100 text-sm">Personalize your email content</p>
                    </div>
                  </div>
                </div>

                {/* Mobile Custom Message Content */}
                <div className="p-4">
                  <div className="text-sm text-gray-600 mb-3">
                    Add your personal touch to the email template
              </div>
              <textarea
                    className="w-full border-2 border-gray-300 rounded-xl px-4 py-3 text-gray-900 bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                placeholder="Enter your custom message here..."
                rows="4"
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                    style={{ fontSize: '16px' }}
              />
                </div>
              </div>
            </div>

            {/* Mobile Email Content Section */}
            <div className="md:hidden bg-white p-4 border-b">
              <div className="bg-white rounded-2xl border-2 border-green-200 overflow-hidden shadow-lg">
                {/* Mobile Email Content Header */}
                <div className="bg-gradient-to-r from-green-500 to-teal-600 text-white p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="bg-white/20 rounded-full p-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-bold text-lg">HTML Email Template</h3>
                        <p className="text-green-100 text-sm">Ready to copy & paste</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Mobile Email Content */}
                <div className="p-4 space-y-4">
                  {/* Mobile Copy Button */}
                <button
                    className={`w-full py-4 px-6 rounded-xl font-bold shadow-lg transition-all flex items-center justify-center gap-3 ${
                    emailCopied 
                      ? 'bg-green-600 text-white' 
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                  onClick={copyEmailTemplate}
                >
                    {emailCopied ? (
                      <>
                        <span className="text-xl">✅</span>
                        <span>Copied HTML Email!</span>
                      </>
                    ) : (
                      <>
                        <span className="text-xl">📋</span>
                        <span>Copy HTML Email</span>
                      </>
                    )}
                </button>

                  {/* Mobile Email Preview - Scrollable */}
                  <div className="bg-gray-50 rounded-xl p-3">
                    <div className="text-xs font-semibold text-gray-700 mb-2">Preview (scroll to see full content):</div>
                    <div className="bg-white border border-gray-200 rounded-lg p-3 max-h-40 overflow-y-auto">
                      <textarea
                        className="w-full h-32 text-xs font-mono bg-transparent border-none resize-none outline-none text-gray-700"
                        value={generateHTMLEmailTemplate()}
                        readOnly
                        onFocus={e => e.target.select()}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Instructions Section */}
            <div className="md:hidden bg-white p-4">
              <div className="bg-white rounded-2xl border-2 border-yellow-200 overflow-hidden shadow-lg">
                {/* Mobile Instructions Header */}
                <div className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white p-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-white/20 rounded-full p-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">💡 How to Use</h3>
                      <p className="text-yellow-100 text-sm">Step-by-step instructions</p>
                    </div>
                  </div>
                </div>

                {/* Mobile Instructions Content */}
                <div className="p-4">
                  <div className="space-y-3">
                    <div className="bg-blue-50 rounded-xl p-3">
                      <div className="text-sm font-semibold text-blue-800 mb-2">📝 Quick Steps:</div>
                      <ol className="text-blue-700 text-sm space-y-2">
                        <li className="flex items-start gap-2">
                          <span className="bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">1</span>
                          <span>Add custom message above (optional)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">2</span>
                          <span>Tap "Copy HTML Email" button</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">3</span>
                          <span>Open Gmail, Outlook, or your email app</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">4</span>
                          <span>Paste content & send to customer</span>
                        </li>
                      </ol>
                    </div>

                    <div className="bg-green-50 rounded-xl p-3">
                      <div className="text-sm font-semibold text-green-800 mb-2">🎯 Send to:</div>
                      <div className="bg-white rounded-lg p-2 border border-green-200">
                        <div className="font-mono text-sm text-gray-900 break-all">
                          {selectedCustomer?.email || 'customer@email.com'}
                        </div>
                      </div>
                    </div>

                    <div className="bg-purple-50 rounded-xl p-3">
                      <div className="text-sm font-semibold text-purple-800 mb-2">✨ Email Features:</div>
                      <ul className="text-purple-700 text-xs space-y-1">
                        <li>• Professional layout with styling</li>
                        <li>• Large clickable "Vehicle Details" button</li>
                        <li>• Vehicle info & pricing display</li>
                        <li>• Your custom message included</li>
                      </ul>
                    </div>
                  </div>

                  {/* Mobile Close Button */}
                  <button
                    className="w-full mt-4 bg-gray-500 text-white py-3 px-4 rounded-xl font-bold hover:bg-gray-600 transition"
                    onClick={() => setShowEmailTemplate(false)}
                  >
                    Close Template
                  </button>
                </div>
              </div>
            </div>

            {/* Desktop Version - Hidden on Mobile */}
            <div className="hidden md:block space-y-6">
              {/* Desktop Custom Message Section */}
              <div className="bg-white rounded-2xl border-2 border-blue-200 overflow-hidden shadow-lg">
                <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-5">
                  <div className="flex items-center gap-4">
                    <div className="bg-white/20 rounded-full p-3">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-xl">✏️ Custom Message</h3>
                      <p className="text-blue-100">Personalize your email content</p>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="text-gray-600 mb-4">
                    Add your personal touch to make the email more engaging for your customer
              </div>
              <textarea
                    className="w-full border-2 border-gray-300 rounded-xl px-4 py-4 text-gray-900 bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-base"
                    placeholder="Enter your custom message here..."
                    rows="4"
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                  />
                </div>
              </div>

              {/* Desktop Email Content Section */}
              <div className="bg-white rounded-2xl border-2 border-green-200 overflow-hidden shadow-lg">
                <div className="bg-gradient-to-r from-green-500 to-teal-600 text-white p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="bg-white/20 rounded-full p-3">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-bold text-xl">📧 HTML Email Template</h3>
                        <p className="text-green-100">Professional email with clickable button</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex justify-between items-center mb-4">
                    <div className="text-gray-600">
                      Complete HTML email ready to copy and paste into your email client
                    </div>
                    <button
                      className={`px-6 py-3 rounded-xl font-bold shadow-lg transition-all flex items-center gap-3 ${
                        emailCopied 
                          ? 'bg-green-600 text-white' 
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                      onClick={copyEmailTemplate}
                    >
                      {emailCopied ? (
                        <>
                          <span className="text-lg">✅</span>
                          <span>Copied HTML Email!</span>
                        </>
                      ) : (
                        <>
                          <span className="text-lg">📋</span>
                          <span>Copy HTML Email</span>
                        </>
                      )}
                    </button>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200">
                    <textarea
                      className="w-full h-96 p-4 border border-gray-300 rounded-lg bg-white text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-blue-200"
                value={generateHTMLEmailTemplate()}
                readOnly
                onFocus={e => e.target.select()}
              />
                  </div>
                </div>
            </div>

              {/* Desktop Instructions */}
              <div className="bg-white rounded-2xl border-2 border-yellow-200 overflow-hidden shadow-lg">
                <div className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white p-5">
                  <div className="flex items-center gap-4">
                    <div className="bg-white/20 rounded-full p-3">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    </div>
                <div>
                      <h3 className="font-bold text-xl">💡 How to Use</h3>
                      <p className="text-yellow-100">Step-by-step instructions for sending</p>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-blue-50 rounded-xl p-4">
                      <h4 className="font-bold text-blue-800 mb-3 text-lg">📝 Quick Steps:</h4>
                      <ol className="text-blue-700 space-y-2">
                        <li className="flex items-start gap-3">
                          <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">1</span>
                          <span>Add your custom message above (optional)</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">2</span>
                          <span>Click "Copy HTML Email" button</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">3</span>
                          <span>Open Gmail, Outlook, or your email client</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">4</span>
                          <span>Create new email and paste content</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">5</span>
                          <span>Switch to "Rich Text" or "HTML" mode</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">6</span>
                          <span>Send to customer!</span>
                        </li>
                  </ol>
                  </div>

                    <div className="space-y-4">
                      <div className="bg-green-50 rounded-xl p-4">
                        <div className="font-bold text-green-800 mb-2 text-lg">🎯 Send to:</div>
                        <div className="bg-white rounded-lg p-3 border-2 border-green-200">
                          <div className="font-mono text-gray-900 break-all text-lg">
                            {selectedCustomer?.email || 'customer@email.com'}
                </div>
              </div>
            </div>

                      <div className="bg-purple-50 rounded-xl p-4">
                        <div className="font-bold text-purple-800 mb-2 text-lg">✨ Email Features:</div>
                        <ul className="text-purple-700 space-y-1">
                          <li className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                            <span>Professional layout with beautiful styling</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                            <span>Large clickable "Vehicle Details" button</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                            <span>Vehicle information and pricing display</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                            <span>Your custom message integration</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Desktop Close Button */}
              <div className="flex justify-end">
              <button
                  className="px-8 py-3 bg-gray-500 text-white rounded-xl font-bold hover:bg-gray-600 transition-all shadow-lg flex items-center gap-2"
                onClick={() => setShowEmailTemplate(false)}
              >
                  <span>✕</span>
                  <span>Close Template</span>
              </button>
              </div>
            </div>
          </div>
        </div>
      )}
        </div>
    </>
  );
} 